import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { isAxiosError, AxiosError } from 'axios';
import { Button, Card, Row, Col, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { FileUp, StickyNote, Edit, Phone, Mail, MapPin, Building2, User, FileText, FileImage, Download, Eye, File, Trash2, Plus, MessageCircle, CreditCard, AlertTriangle, Clock, CheckCircle2, X, Check, MonitorPlay } from 'lucide-react';
import TrDateInput from '../components/TrDateInput';
import IysIzinModal from '../components/IysIzinModal';
import IysKayitListesi, { IysIzinDetay } from '../components/IysKayitListesi';
import IysKilitliAlan from '../components/IysKilitliAlan';
import SmsGonderModal from '../components/SmsGonderModal';
import SmsGecmisi from '../components/SmsGecmisi';

interface Not {
    notID: number;
    eklenmeTarihi: string;
    aciklama: string;
    ekleyenID: number;
    ekleyen: {
        kullaniciID: number;
        adSoyad: string;
    };
    aktifMi: boolean;
    takipNo: string;
}

interface Dosya {
    dosyaID: number;
    takipNo: string;
    eklenmeTarihi: string;
    aktifMi: boolean;
    dosyaAdi: string;
    aciklama: string;
}

interface Sozlesme {
    sozlesmeID: number;
    sozlesmeTakipNo: string;
    sozlesmeTarihi: string;
    rezerveBitisTarihi: string;
    sozlesmeDurumu: string;
    sozlesmeFiyati: number;
    planlananOdeme: number;
    ortalamaVade: number;
    ozelSartlar?: string;
    aktifMi: boolean;
    eklemeTarihi: string;
    cari: {
        cariID: number;
        adiSoyadi: string;
        telefon: string;
        email?: string;
    };
    daire: {
        daireID: number;
        daireNo: string;
        blok?: {
            blokAdi: string;
            proje?: {
                projeID: number;
                projeAdi: string;
            };
        };
    };
}

interface NotForm {
    aciklama: string;
}



const getInitials = (name: string): string =>
    name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();

const AVATAR_STYLES = [
    { background: '#e0e7ff', color: '#4338ca' },
    { background: '#ede9fe', color: '#6d28d9' },
    { background: '#dbeafe', color: '#1d4ed8' },
    { background: '#ccfbf1', color: '#0f766e' },
    { background: '#fef3c7', color: '#b45309' },
    { background: '#ffe4e6', color: '#be123c' },
];

const getAvatarStyle = (id: number) => AVATAR_STYLES[id % AVATAR_STYLES.length];

const formatPhoneNumber = (phone: string): string => {
    // Sadece rakamları al
    const numbers = phone.replace(/\D/g, '');
    
    // 10 haneli telefon numarası için format uygula
    if (numbers.length === 10) {
        return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 8)}-${numbers.slice(8)}`;
    }
    
    // Eğer 10 haneli değilse olduğu gibi döndür
    return phone;
};

const unformatPhoneNumber = (phone: string): string => {
    // Sadece rakamları al
    return phone.replace(/\D/g, '');
};

interface Cari {
    cariID: number;
    takipNo: string;
    eklenmeTarihi: string;
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    kaynak: string | null;
    aktifMi: boolean;
    bakiye: number;
}

interface CariForm {
    adiSoyadi: string;
    firmaAdi: string | null;
    telefon1: string;
    telefon2: string | null;
    ePosta: string | null;
    adres: string | null;
    tc: string | null;
    cariVD: string | null;
    cariVN: string | null;
    turu: string;
    turu2: string | null;
    kaynak: string | null;
    aktifMi: boolean;
}

interface CariAltKategori {
    cariAltKategoriID: number;
    turu: string;
    altKategoriAdi: string;
    aktif: boolean;
}

interface KiraSozlesmeOzet {
    kiraSozlesmeID: number;
    takipNo: string;
    baslangicTarihi: string;
    bitisTarihi: string;
    kiraBedeli: number;
    aktifMi: boolean;
    gayrimenkulBaslik: string | null;
}

interface EmlakSunumOzet {
    sunumID: number;
    takipNo: string;
    baslik: string | null;
    durum: string;
    olusturmaTarihi: string;
    aktifMi: boolean;
}

interface Odeme {
    odemeID: number;
    cariID: number;
    islemTarihi: string;
    vadeTarihi: string | null;
    tahsilatTarihi: string | null;
    miktar: number;
    paraBirimiID: number;
    islemTakipNo: string;
    odemeTuru: string;
    odemeYonu: string;
    odemeDurumu: string;
    aciklama: string | null;
    hizmetUrunAdi: string | null;
    aktifMi: boolean;
    sozlesmeOdemesi: boolean;
    kiraSozlesmesiOdemesi: boolean;
    islemTipi?: string;
    projeID: number | null;
    kasaID: number | null;
    sozlesme?: { sozlesmeID: number; sozlesmeTakipNo: string; projeAdi: string | null; daireNo: string | null; } | null;
    kasa?: { kasaID: number; kasaAdi: string; kasaTuru: string; } | null;
}

interface Kasa {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string;
    aktifMi: boolean;
}

const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const formatMoneyInput = (value: string): string => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers === '') return '';
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseMoneyInput = (value: string): string => value.replace(/\./g, '');

const toTRDate = (iso: string): string => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
};
const fromTRDate = (tr: string): string => {
    if (!tr || tr.length < 10) return '';
    const [d, m, y] = tr.split('.');
    return `${y}-${m}-${d}`;
};
const maskTRDate = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

// UTF-8'in Latin-1 olarak okunmasından kaynaklanan mojibake karakterleri düzeltir
const fixMojibake = (str: string | null | undefined): string | null => {
    if (!str) return str ?? null;
    if (str.split('').some(c => c.charCodeAt(0) > 255)) return str;
    try {
        const bytes = new Uint8Array(str.length);
        for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i) & 0xFF;
        const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
        if (decoded.length < str.length) return decoded;
        return str;
    } catch { return str; }
};

const MusteriDetay: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const musteriCardRef = useRef<HTMLDivElement>(null);
    const [notlarMaxHeight, setNotlarMaxHeight] = useState<number>(600);
    const [musteri, setMusteri] = useState<Cari | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [iysIzinlerMd, setIysIzinlerMd] = useState<IysIzinDetay[]>([]);
    const [iysModalMd, setIysModalMd] = useState<{iletisimTipi:'TELEFON1'|'TELEFON2'|'EPOSTA';iletisimVerisi:string}|null>(null);
    const [iysAktarimAktif, setIysAktarimAktif] = useState(false);
    const [activeFormTab, setActiveFormTab] = useState<'temel' | 'firma'>('temel');
    const [leadKaynaklar, setLeadKaynaklar] = useState<{ id: number; ad: string; aktifMi: boolean }[]>([]);
    const [activeMainTab, setActiveMainTab] = useState<'sozlesmeler' | 'finans' | 'sunumlar' | 'aktiviteler' | 'dosyalar' | 'smsler'>('sozlesmeler');
    // Müşteri sunumları (Projeler > Müşteri Sunumları kayıtları)
    const [musteriSunumlari, setMusteriSunumlari] = useState<any[]>([]);
    const [sunumlarLoading, setSunumlarLoading] = useState(false);
    const [sunumOlusturuluyor, setSunumOlusturuluyor] = useState(false);
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [smsRefreshKey, setSmsRefreshKey] = useState(0);
    const [showNotModal, setShowNotModal] = useState(false);
    const [showDosyaModal, setShowDosyaModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [previewType, setPreviewType] = useState<string>('');
    const { user } = useAuth();
    const [notlar, setNotlar] = useState<Not[]>([]);
    const [dosyalar, setDosyalar] = useState<Dosya[]>([]);
    const [sozlesmeler, setSozlesmeler] = useState<Sozlesme[]>([]);
    const [odemeler, setOdemeler] = useState<Odeme[]>([]);
    const [showOdemeEditModal, setShowOdemeEditModal] = useState(false);
    const [selectedOdeme, setSelectedOdeme] = useState<Odeme | null>(null);
    const [kasalar, setKasalar] = useState<Kasa[]>([]);
    const [odemeListeFiltre, setOdemeListeFiltre] = useState<string>('');
    const [showIslemlerMenu, setShowIslemlerMenu] = useState(false);
    const [showHareketModal, setShowHareketModal] = useState(false);
    const [hareketSaving, setHareketSaving] = useState(false);
    const [hareketFormData, setHareketFormData] = useState({
        miktar: '',
        vadeTarihi: '',
        odemeTuru: 'Nakit',
        odemeYonu: 'Tahsilat',
        odemeDurumu: 'Bekliyor',
        aciklama: '',
        kasaID: '',
        sozlesmeSecimi: '',
        kiraSozlesmeSecimi: '',
    });
    const [odemeEditForm, setOdemeEditForm] = useState({
        vadeTarihi: '',
        tahsilatTarihi: '',
        miktar: 0,
        odemeDurumu: '',
        odemeTuru: '',
        aciklama: '',
        kasaID: null as number | null,
    });
    const [saving, setSaving] = useState(false);
    const [activeHareketTab, setActiveHareketTab] = useState<'ekstre' | 'plan'>('ekstre');
    const [hareketFilters, setHareketFilters] = useState({ vadeTarihiBaslangic: '', vadeTarihiBitis: '', odemeTuru: '', odemeDurumu: '' });
    const [selectedTaksitler, setSelectedTaksitler] = useState<Set<number>>(new Set());
    const [taksitTopluSilLoading, setTaksitTopluSilLoading] = useState(false);
    const [odemePlaniSozlesmeFiltre, setOdemePlaniSozlesmeFiltre] = useState<string>('');
    const [showTopluPlanlaModal, setShowTopluPlanlaModal] = useState(false);
    const [topluPlanlaFormData, setTopluPlanlaFormData] = useState({ toplamTutar: '', baslamaTarihi: '', taksitSayisi: '', aralikAy: '1', odemeTuru: 'Nakit', aciklama: '' });
    const [showOdemeYapModal, setShowOdemeYapModal] = useState(false);
    const [odemeYapTaksit, setOdemeYapTaksit] = useState<Odeme | null>(null);
    const [odemeYapFormData, setOdemeYapFormData] = useState({ tarih: '', kasaID: '', odemeTuru: 'Nakit', sozlesmeSecimi: '', kiraSozlesmeSecimi: '', aciklama: '' });
    const [odemeYapTarihDisplay, setOdemeYapTarihDisplay] = useState('');
    const [sozlesmePlanTaksitleri, setSozlesmePlanTaksitleri] = useState<any[]>([]);
    const [isPlanTaksitOdeme, setIsPlanTaksitOdeme] = useState(false);
    const [planTaksitForOdeme, setPlanTaksitForOdeme] = useState<any>(null);
    const [relatedRecords, setRelatedRecords] = useState<{ kiraSozlesmeleri: KiraSozlesmeOzet[]; emlakSunumlari: EmlakSunumOzet[] }>({ kiraSozlesmeleri: [], emlakSunumlari: [] });
    const [notFormData, setNotFormData] = useState<NotForm>({
        aciklama: ''
    });
    const [dosyaFormData, setDosyaFormData] = useState({
        dosya: null as File | null,
        aciklama: ''
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchNotlar = async () => {
        if (musteri?.takipNo) {
            try {
                const response = await axiosInstance.get(`/api/Notlar/takip/${musteri.takipNo}`);
                console.log('Notlar response:', response.data);
                setNotlar(response.data);
            } catch (error) {
                console.error('Notlar error:', error);
                toast.error(t('msg.islemHatasi'));
            }
        }
    };

    const fetchDosyalar = async () => {
        if (musteri?.takipNo && musteri.takipNo !== '') {
            try {
                const response = await axiosInstance.get(`/api/Dosyalar/takip/${musteri.takipNo}`, {
                    params: {
                        dosyaTuru: 1 // Müşteri dosyaları
                    }
                });
                setDosyalar(response.data);
            } catch (error: unknown) {
                console.error('Dosyalar yüklenirken hata:', error);
                // Sadece 404 dışındaki hataları göster
                if (isAxiosError(error) && error.response?.status !== 404) {
                    toast.error(t('msg.islemHatasi'));
                }
            }
        } else {
            // Yeni müşteri oluşturma durumunda dosya listesini temizle
            setDosyalar([]);
        }
    };

    const fetchMusteriSunumlari = async () => {
        if (!musteri?.cariID) { setMusteriSunumlari([]); return; }
        try {
            setSunumlarLoading(true);
            const res = await axiosInstance.get(`/api/MusteriSunumlari?cariID=${musteri.cariID}&aktifMi=true`);
            setMusteriSunumlari(Array.isArray(res.data) ? res.data : []);
        } catch {
            setMusteriSunumlari([]);
        } finally {
            setSunumlarLoading(false);
        }
    };

    /** Bu müşteri için doğrudan yeni sunum oluşturur.
     *  Taslak/Devam Ediyor durumunda sunum varsa backend 409 döner; yeni sunum açılmaz. */
    const handleSunumOlustur = async () => {
        if (!musteri?.cariID) return;
        const devamEden = musteriSunumlari.find(s => s.durum === 'Taslak' || s.durum === 'Devam Ediyor');
        if (devamEden) {
            toast.warning(`Bu müşterinin "${devamEden.durum}" durumunda bir sunumu var. Lütfen devam eden sunumu tamamlayınız.`, { autoClose: 6000 });
            navigate(`/musteri-sunumlari/${devamEden.sunumID}`);
            return;
        }
        try {
            setSunumOlusturuluyor(true);
            const res = await axiosInstance.post('/api/MusteriSunumlari', {
                cariID: musteri.cariID,
                olusturanKullaniciID: user?.kullaniciID,
                baslik: `${musteri.adiSoyadi} Sunumu`,
                daireIDler: [],
            });
            toast.success('Sunum oluşturuldu.');
            navigate(`/musteri-sunumlari/${res.data.sunumID}`);
        } catch (err: any) {
            if (err?.response?.status === 409) {
                const d = err.response.data;
                toast.warning(d?.message || 'Bu müşterinin devam eden bir sunumu var.', { autoClose: 6000 });
                if (d?.mevcutSunumID) navigate(`/musteri-sunumlari/${d.mevcutSunumID}`);
            } else {
                toast.error('Sunum oluşturulamadı.');
            }
        } finally {
            setSunumOlusturuluyor(false);
        }
    };

    const fetchSozlesmeler = async () => {
        if (musteri?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/Sozlesmeler?cariID=${musteri.cariID}&aktifMi=true`);
                setSozlesmeler(response.data);
            } catch (error: unknown) {
                console.error('Sözleşmeler yüklenirken hata:', error);
                if (isAxiosError(error) && error.response?.status !== 404) {
                    toast.error(t('msg.islemHatasi'));
                }
            }
        } else {
            setSozlesmeler([]);
        }
    };

    const fetchOdemeler = async () => {
        if (musteri?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/Odemeler?cariID=${musteri.cariID}&aktifMi=true`);
                setOdemeler(response.data);
            } catch (error: unknown) {
                console.error('Ödemeler yüklenirken hata:', error);
            }
        } else {
            setOdemeler([]);
        }
    };

    const fetchKasalar = async () => {
        try {
            const response = await axiosInstance.get('/api/Kasalar?aktifMi=true');
            setKasalar(response.data);
        } catch {
            // sessiz hata
        }
    };

    const getCurrentDate = (): string => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const isTaksitOdeme = (o: Odeme) => /^\d+\.\s*taksit/i.test(o.aciklama || '');
    const isTediyeOdeme = (o: Odeme) => /^Tediye\s*·/i.test(o.aciklama || '');
    const isSozlesmeGeliri = (o: Odeme) => o.islemTakipNo?.startsWith('GEL-SOZ-');
    const isKiraGeliri = (o: Odeme) => o.islemTakipNo?.startsWith('GEL-KRA-');

    const handleTahsilatEkle = () => {
        setHareketFormData({
            miktar: '', vadeTarihi: getCurrentDate(), odemeTuru: 'Nakit',
            odemeYonu: 'Tahsilat', odemeDurumu: 'Bekliyor', aciklama: '',
            kasaID: '', sozlesmeSecimi: '', kiraSozlesmeSecimi: ''
        });
        setShowHareketModal(true);
    };

    const handleOdemeEkle = () => {
        setHareketFormData({
            miktar: '', vadeTarihi: getCurrentDate(), odemeTuru: 'Nakit',
            odemeYonu: 'Odeme', odemeDurumu: 'Bekliyor', aciklama: '',
            kasaID: '', sozlesmeSecimi: '', kiraSozlesmeSecimi: ''
        });
        setShowHareketModal(true);
    };

    const handleSaveHareket = async () => {
        if (!hareketFormData.miktar || !hareketFormData.vadeTarihi || !musteri?.cariID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        if (hareketFormData.odemeDurumu === 'Odendi' && !hareketFormData.kasaID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        const toOdemeTuru = (t: string): number => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 }[t] ?? 0);
        const toOdemeYonu = (y: string): number => (y === 'Odeme' ? 1 : 0);
        const toOdemeDurumu = (d: string): number => (d === 'Odendi' ? 1 : 0);

        let islemTakipNo = musteri.takipNo;
        let sozlesmeOdemesi = false;
        let kiraSozlesmesiOdemesi = false;

        if (hareketFormData.sozlesmeSecimi) {
            islemTakipNo = hareketFormData.sozlesmeSecimi;
            sozlesmeOdemesi = true;
        } else if (hareketFormData.kiraSozlesmeSecimi) {
            islemTakipNo = hareketFormData.kiraSozlesmeSecimi;
            kiraSozlesmesiOdemesi = true;
        }

        try {
            setHareketSaving(true);
            await axiosInstance.post('/api/Odemeler', {
                cariID: musteri.cariID,
                islemTarihi: getCurrentDate(),
                vadeTarihi: hareketFormData.vadeTarihi,
                tahsilatTarihi: hareketFormData.vadeTarihi,
                miktar: parseFloat(hareketFormData.miktar),
                paraBirimiID: 1,
                islemTakipNo,
                odemeTuru: toOdemeTuru(hareketFormData.odemeTuru),
                odemeYonu: toOdemeYonu(hareketFormData.odemeYonu),
                islemTipi: toOdemeYonu(hareketFormData.odemeYonu) === 0 ? 2 : 3,
                odemeDurumu: toOdemeDurumu(hareketFormData.odemeDurumu),
                kasaID: hareketFormData.kasaID ? parseInt(hareketFormData.kasaID) : null,
                aciklama: hareketFormData.aciklama || null,
                hizmetUrunAdi: null,
                projeID: null,
                aktifMi: true,
                sozlesmeOdemesi,
                kiraSozlesmesiOdemesi,
                eklemeTarihi: new Date().toISOString()
            });
            toast.success(t('msg.islemBasarili'));
            setShowHareketModal(false);
            fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setHareketSaving(false);
        }
    };

    const handleOdemeEditAc = (odeme: Odeme) => {        setSelectedOdeme(odeme);
        setOdemeEditForm({
            vadeTarihi: odeme.vadeTarihi ? odeme.vadeTarihi.substring(0, 10) : '',
            tahsilatTarihi: odeme.tahsilatTarihi ? odeme.tahsilatTarihi.substring(0, 10) : '',
            miktar: odeme.miktar,
            odemeDurumu: odeme.odemeDurumu,
            odemeTuru: odeme.odemeTuru,
            aciklama: odeme.aciklama || '',
            kasaID: odeme.kasaID,
        });
        setShowOdemeEditModal(true);
    };

    const handleOdemeGuncelle = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedOdeme) return;
        if (odemeEditForm.odemeDurumu === 'Odendi' && !odemeEditForm.kasaID) {
            toast.error(t('msg.zorunluAlanlar'));
            return;
        }
        try {
            await axiosInstance.put(`/api/Odemeler/${selectedOdeme.odemeID}`, {
                odemeID: selectedOdeme.odemeID,
                cariID: selectedOdeme.cariID,
                islemTarihi: selectedOdeme.islemTarihi,
                vadeTarihi: odemeEditForm.vadeTarihi || null,
                tahsilatTarihi: odemeEditForm.tahsilatTarihi || null,
                miktar: odemeEditForm.miktar,
                paraBirimiID: selectedOdeme.paraBirimiID,
                islemTakipNo: selectedOdeme.islemTakipNo,
                odemeTuru: odemeEditForm.odemeTuru,
                odemeYonu: selectedOdeme.odemeYonu,
                odemeDurumu: odemeEditForm.odemeDurumu,
                aciklama: odemeEditForm.aciklama || null,
                hizmetUrunAdi: selectedOdeme.hizmetUrunAdi,
                aktifMi: selectedOdeme.aktifMi,
                sozlesmeOdemesi: selectedOdeme.sozlesmeOdemesi,
                projeID: selectedOdeme.projeID,
                kasaID: odemeEditForm.kasaID,
            });
            toast.success(t('msg.islemBasarili'));
            setShowOdemeEditModal(false);
            fetchOdemeler();
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleShowTopluPlanlaModal = () => {
        setTopluPlanlaFormData({ toplamTutar: '', baslamaTarihi: toTRDate(getCurrentDate()), taksitSayisi: '', aralikAy: '1', odemeTuru: 'Nakit', aciklama: '' });
        setShowTopluPlanlaModal(true);
    };

    const handleSaveTopluPlanla = async () => {
        const { toplamTutar, baslamaTarihi: baslamaTarihiTR, taksitSayisi, aralikAy, odemeTuru, aciklama } = topluPlanlaFormData;
        const baslamaTarihi = fromTRDate(baslamaTarihiTR);
        if (!toplamTutar || !baslamaTarihi || !taksitSayisi || !aralikAy) { toast.error(t('msg.zorunluAlanlar')); return; }
        if (!musteri?.cariID) { toast.error('M\u00fc\u015fteri bilgisi bulunamad\u0131!'); return; }
        const sayi = parseInt(taksitSayisi);
        const tutar = parseFloat(parseMoneyInput(toplamTutar));
        const aralik = parseInt(aralikAy);
        if (sayi <= 0 || tutar <= 0 || aralik <= 0) { toast.error(t('msg.zorunluAlanlar')); return; }
        const toOT = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string,number>)[t] ?? 0;
        const taksitTutari = tutar / sayi;
        let kalanTutar = tutar;
        try {
            setSaving(true);
            const promises = [];
            for (let i = 0; i < sayi; i++) {
                const vade = new Date(baslamaTarihi);
                vade.setMonth(vade.getMonth() + i * aralik);
                const miktarBu = i === sayi - 1 ? Math.round(kalanTutar * 100) / 100 : Math.round(taksitTutari * 100) / 100;
                if (i < sayi - 1) kalanTutar -= miktarBu;
                promises.push(axiosInstance.post('/api/Odemeler', {
                    cariID: musteri.cariID, islemTarihi: baslamaTarihi,
                    vadeTarihi: vade.toISOString().split('T')[0], tahsilatTarihi: vade.toISOString().split('T')[0],
                    miktar: miktarBu, paraBirimiID: 1, islemTakipNo: musteri.takipNo,
                    odemeTuru: toOT(odemeTuru), odemeYonu: 0, islemTipi: 2, odemeDurumu: 0,
                    aciklama: `${i + 1}. Taksit${aciklama ? ' - ' + aciklama : ''}`,
                    aktifMi: true, sozlesmeOdemesi: false, eklemeTarihi: new Date().toISOString()
                }));
            }
            await Promise.all(promises);
            toast.success(`${sayi} adet taksit ba\u015far\u0131yla olu\u015fturuldu!`);
            setShowTopluPlanlaModal(false);
            await fetchOdemeler();
        } catch { toast.error('Taksitler olu\u015fturulurken bir hata olu\u015ftu!'); }
        finally { setSaving(false); }
    };

    const fetchSozlesmePlanTaksitleri = async () => {
        if (!musteri?.cariID) return;
        try {
            const res = await axiosInstance.get(`/api/OdemePlanTaksitleri?cariID=${musteri.cariID}`);
            setSozlesmePlanTaksitleri(res.data);
        } catch {
            setSozlesmePlanTaksitleri([]);
        }
    };

    const handlePlanTaksitTahsilatYap = (taksit: any) => {
        setIsPlanTaksitOdeme(true);
        setPlanTaksitForOdeme(taksit);
        setOdemeYapTaksit(null);
        setOdemeYapFormData({ tarih: getCurrentDate(), kasaID: '', odemeTuru: 'Nakit', sozlesmeSecimi: '', kiraSozlesmeSecimi: '', aciklama: taksit.aciklama || '' });
        setOdemeYapTarihDisplay(toTRDate(getCurrentDate()));
        setShowOdemeYapModal(true);
    };

    const handlePlanTaksitGeriAl = async (taksit: any) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            setSaving(true);
            if (taksit.gercekOdemeID) {
                await axiosInstance.post(`/api/Odemeler/delete/${taksit.gercekOdemeID}`);
            }
            await axiosInstance.put(`/api/OdemePlanTaksitleri/${taksit.taksitID}/geriali`);
            toast.success(t('msg.islemBasarili'));
            await fetchOdemeler();
            await fetchSozlesmePlanTaksitleri();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeletePlanTaksit = async (taksit: any) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/OdemePlanTaksitleri/${taksit.taksitID}`);
            await fetchSozlesmePlanTaksitleri();
            toast.success(t('msg.islemBasarili'));
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleOdemeYap = (taksit: Odeme) => {
        setIsPlanTaksitOdeme(false);
        setPlanTaksitForOdeme(null);
        setOdemeYapTaksit(taksit);
        setOdemeYapFormData({ tarih: getCurrentDate(), kasaID: '', odemeTuru: 'Nakit', sozlesmeSecimi: '', kiraSozlesmeSecimi: '', aciklama: taksit.aciklama || '' });
        setOdemeYapTarihDisplay(toTRDate(getCurrentDate()));
        setShowOdemeYapModal(true);
    };

    const handleSaveOdemeYap = async () => {
        // Sözleşme plan taksiti tahsilatı
        if (isPlanTaksitOdeme && planTaksitForOdeme) {
            if (!odemeYapFormData.kasaID) { toast.error(t('msg.zorunluAlanlar')); return; }
            const toOTPlan = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[t] ?? 0;
            try {
                setSaving(true);
                const odemeRes = await axiosInstance.post('/api/Odemeler', {
                    cariID: musteri!.cariID,
                    islemTarihi: odemeYapFormData.tarih,
                    vadeTarihi: planTaksitForOdeme.vadeTarihi,
                    tahsilatTarihi: odemeYapFormData.tarih,
                    miktar: planTaksitForOdeme.miktar,
                    paraBirimiID: 1,
                    islemTakipNo: planTaksitForOdeme.sozlesmeTakipNo,
                    odemeTuru: toOTPlan(odemeYapFormData.odemeTuru),
                    odemeYonu: 0,
                    odemeDurumu: 1,
                    kasaID: parseInt(odemeYapFormData.kasaID),
                    aciklama: odemeYapFormData.aciklama || planTaksitForOdeme.aciklama,
                    aktifMi: true,
                    sozlesmeOdemesi: true,
                    eklemeTarihi: new Date().toISOString(),
                    islemTipi: 2,
                });
                const yeniOdemeID = odemeRes.data?.odemeID;
                await axiosInstance.put(`/api/OdemePlanTaksitleri/${planTaksitForOdeme.taksitID}/tahsilat`, {
                    gercekOdemeID: yeniOdemeID,
                    tahsilatTarihi: odemeYapFormData.tarih,
                    kasaID: parseInt(odemeYapFormData.kasaID),
                });
                toast.success(t('msg.islemBasarili'));
                setShowOdemeYapModal(false);
                setIsPlanTaksitOdeme(false);
                setPlanTaksitForOdeme(null);
                await fetchOdemeler();
                await fetchSozlesmePlanTaksitleri();
            } catch { toast.error(t('msg.islemHatasi')); }
            finally { setSaving(false); }
            return;
        }
        if (!odemeYapTaksit || !musteri?.cariID) return;
        if (!odemeYapFormData.kasaID) { toast.error('L\u00fctfen kasa veya banka se\u00e7in!'); return; }
        const toOT = (t: string) => ({ Nakit: 0, BankaHavale: 1, Cek: 2, Senet: 3, Takas: 4, Mahsup: 5 } as Record<string, number>)[t] ?? 0;
        let islemTakipNo = musteri.takipNo;
        let sozlesmeOdemesi = false;
        let kiraSozlesmesiOdemesi = false;
        if (odemeYapFormData.sozlesmeSecimi) {
            islemTakipNo = odemeYapFormData.sozlesmeSecimi;
            sozlesmeOdemesi = true;
        } else if (odemeYapFormData.kiraSozlesmeSecimi) {
            islemTakipNo = odemeYapFormData.kiraSozlesmeSecimi;
            kiraSozlesmesiOdemesi = true;
        } else if (odemeYapTaksit.kiraSozlesmesiOdemesi) {
            islemTakipNo = odemeYapTaksit.islemTakipNo;
            kiraSozlesmesiOdemesi = true;
        } else if (odemeYapTaksit.sozlesmeOdemesi) {
            islemTakipNo = odemeYapTaksit.islemTakipNo;
            sozlesmeOdemesi = true;
        }
        try {
            setSaving(true);
            // Taksiti Odendi olarak işaretle
            await axiosInstance.put(`/api/Odemeler/${odemeYapTaksit.odemeID}`, {
                odemeID: odemeYapTaksit.odemeID, cariID: odemeYapTaksit.cariID,
                islemTarihi: odemeYapTaksit.islemTarihi, vadeTarihi: odemeYapTaksit.vadeTarihi,
                tahsilatTarihi: odemeYapFormData.tarih, miktar: odemeYapTaksit.miktar,
                paraBirimiID: odemeYapTaksit.paraBirimiID, islemTakipNo: odemeYapTaksit.islemTakipNo,
                odemeTuru: toOT(odemeYapFormData.odemeTuru), odemeYonu: odemeYapTaksit.odemeYonu === 'Odeme' ? 1 : 0, islemTipi: odemeYapTaksit.odemeYonu === 'Odeme' ? 3 : 2, odemeDurumu: 1,
                kasaID: parseInt(odemeYapFormData.kasaID),
                aciklama: odemeYapTaksit.aciklama, hizmetUrunAdi: null,
                aktifMi: true, sozlesmeOdemesi: false, eklemeTarihi: new Date().toISOString(),
                projeID: odemeYapTaksit.projeID,
            });
            toast.success('Tahsilat ba\u015far\u0131yla kaydedildi!');
            setShowOdemeYapModal(false);
            setOdemeYapTaksit(null);
            await fetchOdemeler();
        } catch { toast.error('Tahsilat kaydedilirken bir hata olu\u015ftu!'); }
        finally { setSaving(false); }
    };

    const handleTahsilatGeriAl = async (taksit: Odeme) => {
        if (!window.confirm(`"${taksit.aciklama}" taksitinin tahsilat\u0131 geri al\u0131nacak. Devam etmek istiyor musunuz?`)) return;
        try {
            setSaving(true);
            await axiosInstance.put(`/api/Odemeler/${taksit.odemeID}`, {
                odemeID: taksit.odemeID,
                cariID: taksit.cariID,
                islemTarihi: taksit.islemTarihi,
                vadeTarihi: taksit.vadeTarihi,
                tahsilatTarihi: null,
                miktar: taksit.miktar,
                paraBirimiID: taksit.paraBirimiID,
                islemTakipNo: taksit.islemTakipNo,
                odemeTuru: ({ Nakit: 0, Senet: 1, Cek: 2, Takas: 3 } as Record<string, number>)[taksit.odemeTuru] ?? 0,
                odemeYonu: 0,
                odemeDurumu: 0,
                kasaID: null,
                aciklama: taksit.aciklama,
                hizmetUrunAdi: null,
                aktifMi: true,
                sozlesmeOdemesi: false,
                projeID: taksit.projeID,
            });
            toast.success(t('msg.islemBasarili'));
            await fetchOdemeler();
        } catch {
            toast.error('Tahsilat geri alma i\u015flemi s\u0131ras\u0131nda bir hata olu\u015ftu!');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTaksit = async (odeme: Odeme) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.put(`/api/Odemeler/${odeme.odemeID}`, {
                odemeID: odeme.odemeID, cariID: odeme.cariID, islemTarihi: odeme.islemTarihi,
                vadeTarihi: odeme.vadeTarihi, tahsilatTarihi: odeme.tahsilatTarihi, miktar: odeme.miktar,
                paraBirimiID: odeme.paraBirimiID, islemTakipNo: odeme.islemTakipNo,
                odemeTuru: odeme.odemeTuru, odemeYonu: odeme.odemeYonu,
                odemeDurumu: odeme.odemeDurumu, aciklama: odeme.aciklama,
                hizmetUrunAdi: odeme.hizmetUrunAdi, aktifMi: false,
                sozlesmeOdemesi: odeme.sozlesmeOdemesi, eklemeTarihi: new Date().toISOString(),
                projeID: odeme.projeID, kasaID: odeme.kasaID ?? null,
            });
            toast.success('Taksit silindi.');
            await fetchOdemeler();
        } catch { toast.error('Silme i\u015flemi s\u0131ras\u0131nda bir hata olu\u015ftu!'); }
    };

    const handleTopluSilTaksit = async () => {
        if (selectedTaksitler.size === 0) return;
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        setTaksitTopluSilLoading(true);
        try {
            await Promise.all(Array.from(selectedTaksitler).map(id => {
                const odeme = odemeler.find(o => o.odemeID === id);
                if (!odeme) return Promise.resolve();
                return axiosInstance.put(`/api/Odemeler/${id}`, {
                    odemeID: odeme.odemeID, cariID: odeme.cariID, islemTarihi: odeme.islemTarihi,
                    vadeTarihi: odeme.vadeTarihi, tahsilatTarihi: odeme.tahsilatTarihi, miktar: odeme.miktar,
                    paraBirimiID: odeme.paraBirimiID, islemTakipNo: odeme.islemTakipNo,
                    odemeTuru: odeme.odemeTuru, odemeYonu: odeme.odemeYonu,
                    odemeDurumu: odeme.odemeDurumu, aciklama: odeme.aciklama,
                    hizmetUrunAdi: odeme.hizmetUrunAdi, aktifMi: false,
                    sozlesmeOdemesi: odeme.sozlesmeOdemesi, eklemeTarihi: new Date().toISOString(),
                    projeID: odeme.projeID, kasaID: odeme.kasaID ?? null,
                });
            }));
            toast.success(`${selectedTaksitler.size} taksit silindi.`);
            setSelectedTaksitler(new Set());
            await fetchOdemeler();
        } catch { toast.error('Toplu silme s\u0131ras\u0131nda bir hata olu\u015ftu!'); }
        finally { setTaksitTopluSilLoading(false); }
    };

    const fetchRelatedRecords = async () => {
        if (musteri?.cariID) {
            try {
                const response = await axiosInstance.get(`/api/Cariler/${musteri.cariID}/related`);
                setRelatedRecords(response.data);
            } catch {
                // sessiz hata
            }
        }
    };

    const isPreviewable = (fileName: string): boolean => {
        const ext = fileName.toLowerCase().split('.').pop();
        return ['pdf', 'jpg', 'jpeg', 'png'].includes(ext || '');
    };

    const handlePreview = async (dosyaID: number, dosyaAdi: string) => {
        try {
            const ext = dosyaAdi.toLowerCase().split('.').pop() || '';
            const isPdf = ext === 'pdf';
            const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);

            if (!isPdf && !isImage) {
                toast.info(t('msg.bilgi'));
                handleDosyaDownload(dosyaID);
                return;
            }

            const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
                responseType: 'blob',
                headers: {
                    'Accept': isPdf ? 'application/pdf' : 'image/*'
                }
            });
            
            const contentType = response.headers['content-type'];
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            
            setPreviewUrl(url);
            setPreviewType(contentType);
            setShowPreviewModal(true);
        } catch (error) {
            console.error('Dosya önizleme hatası:', error);
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleDosyaDownload = async (dosyaID: number) => {
        try {
            const response = await axiosInstance.get(`/api/Dosyalar/download/${dosyaID}`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/octet-stream'
                }
            });
            
            // Content-Disposition header'ından dosya adını al
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'indirilen-dosya';
            
            if (contentDisposition) {
                // RFC 5987 encoding için kontrol
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                    // URL-encoded dosya adını decode et
                    try {
                        fileName = decodeURIComponent(fileName);
                    } catch (e) {
                        console.warn('Dosya adı decode edilemedi:', e);
                    }
                }
            }

            // Dosyayı indir
            const url = window.URL.createObjectURL(new Blob([response.data], { 
                type: response.headers['content-type'] 
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Dosya indirme hatası:', error);
            toast.error(t('msg.islemHatasi'));
        }
    };
    const [formData, setFormData] = useState<CariForm>({
        adiSoyadi: '',
        firmaAdi: null,
        telefon1: '',
        telefon2: null,
        ePosta: null,
        adres: null,
        tc: null,
        cariVD: null,
        cariVN: null,
        turu: 'Müşteri',
        turu2: null,
        kaynak: null,
        aktifMi: true
    });

    const [cariAltKategoriler, setCariAltKategoriler] = useState<CariAltKategori[]>([]);

    const fetchCariAltKategoriler = async () => {
        try {
            const response = await axiosInstance.get('/api/CariAltKategorileri/aktif?turu=Müşteri');
            setCariAltKategoriler(response.data);
        } catch {
            // sessiz hata
        }
    };

    const fetchMusteri = async () => {
        if (id === 'yeni') {
            setMusteri({
                cariID: 0,
                takipNo: '',
                eklenmeTarihi: new Date().toISOString(),
                adiSoyadi: '',
                firmaAdi: null,
                telefon1: '',
                telefon2: null,
                ePosta: null,
                adres: null,
                tc: null,
                cariVD: null,
                cariVN: null,
                turu: 'Müşteri',
                turu2: null,
                kaynak: null,
                aktifMi: true,
                bakiye: 0
            });
            setFormData({
                adiSoyadi: '',
                firmaAdi: null,
                telefon1: '',
                telefon2: null,
                ePosta: null,
                adres: null,
                tc: null,
                cariVD: null,
                cariVN: null,
                turu: 'Müşteri',
                turu2: null,
                kaynak: null,
                aktifMi: true
            });
            setActiveFormTab('temel');
            setShowModal(true);
        } else {
            try {
                const response = await axiosInstance.get(`/api/Cariler/${id}`);
                const fixedAdres = fixMojibake(response.data.adres);
                if (response.data.turu !== 'Müşteri') {
                    navigate(`/cariler/${id}`, { replace: true });
                    return;
                }
                setMusteri({ ...response.data, adres: fixedAdres });
                fetchNotlar();
                setFormData({
                    adiSoyadi: response.data.adiSoyadi,
                    firmaAdi: response.data.firmaAdi,
                    telefon1: response.data.telefon1,
                    telefon2: response.data.telefon2,
                    ePosta: response.data.ePosta,
                    adres: fixedAdres,
                    tc: response.data.tc,
                    cariVD: response.data.cariVD,
                    cariVN: response.data.cariVN,
                    turu: response.data.turu,
                    turu2: response.data.turu2,
                    kaynak: response.data.kaynak,
                    aktifMi: response.data.aktifMi
                });
            } catch (error) {
                toast.error(t('msg.islemHatasi'));
                navigate('/musteriler');
            }
        }
    };

    useEffect(() => {
        fetchMusteri();
        fetchCariAltKategoriler();
        axiosInstance.get('/api/IysAktarimAyarlari/aktif').then(r => setIysAktarimAktif(r.data.aktif)).catch(() => {});
        axiosInstance.get('/api/LeadKaynaklar').then(r => setLeadKaynaklar(r.data)).catch(() => {});
    }, [id]);

    useEffect(() => {
        if (musteri?.takipNo) {
            fetchNotlar();
            fetchDosyalar();
        }
        if (musteri?.cariID) {
            fetchSozlesmeler();
            fetchOdemeler();
            fetchSozlesmePlanTaksitleri();
            fetchRelatedRecords();
            fetchKasalar();
            fetchMusteriSunumlari();
        }
    }, [musteri?.takipNo, musteri?.cariID]);

    // Müşteri kartının yüksekliğini izle
    useEffect(() => {
        const updateNotlarHeight = () => {
            if (musteriCardRef.current) {
                const cardHeight = musteriCardRef.current.offsetHeight;
                // Card.Header yüksekliğini düş (yaklaşık 50px)
                setNotlarMaxHeight(cardHeight - 50);
            }
        };

        // İlk yükleme ve pencere boyutu değiştiğinde güncelle
        updateNotlarHeight();
        window.addEventListener('resize', updateNotlarHeight);

        // Cleanup
        return () => {
            window.removeEventListener('resize', updateNotlarHeight);
        };
    }, [musteri]); // musteri değiştiğinde de kontrol et

    const handleNotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            console.log('Submitting note with data:', {
                aciklama: notFormData.aciklama,
                ekleyenID: user?.kullaniciID,
                aktifMi: true,
                takipNo: musteri?.takipNo
            });
                await axiosInstance.post('/api/Notlar', {
                aciklama: notFormData.aciklama,
                ekleyenID: user?.kullaniciID,
                aktifMi: true,
                takipNo: musteri?.takipNo
            });
            toast.success(t('msg.islemBasarili'));
            setShowNotModal(false);
            setNotFormData({ aciklama: '' });
            fetchNotlar();
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error(t('msg.islemHatasi'));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (id === 'yeni') {
                const response = await axiosInstance.post('/api/Cariler', formData);
                toast.success(t('msg.islemBasarili'));
                setShowModal(false);
                navigate(`/musteriler/${response.data.cariID}`);
            } else {
                await axiosInstance.put(`/api/Cariler/${id}`, formData);
                toast.success(t('msg.islemBasarili'));
                fetchMusteri();
                setShowModal(false);
            }
        } catch (error: any) {
            const msg = error?.response?.data?.message;
            toast.error(msg || (id === 'yeni' ? 'Müşteri eklenirken bir hata oluştu' : 'Güncelleme sırasında bir hata oluştu'));
        }
    };

    if (!musteri) {
        return <div>{t('msg.yukleniyor')}</div>;
    }

    return (
        <div>
            <section className="content" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Geri Dön */}
                    <div style={{ marginBottom: 16 }}>
                        <button
                            onClick={() => navigate(-1)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '12px', fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'color 0.15s', letterSpacing: '0.01em' }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#0f172a')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                        >
                            <i className="fas fa-arrow-left" style={{ fontSize: '10px' }}></i> {t('btn.geriDon')}
                        </button>
                    </div>
                    {/* Hero Card - Birleşik Kart */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '1.25rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                                overflow: 'hidden'
                            }}>
                                {/* Üst Kısım - Avatar ve Bilgiler */}
                                <div style={{
                                    padding: '2rem 2rem 1.5rem',
                                    borderBottom: '1px solid #f1f5f9'
                                }}>
                                    <div className="d-flex align-items-start justify-content-between gap-4 flex-wrap">
                                        <div className="d-flex align-items-start gap-4 flex-1">
                                            {/* İsim ve Durum */}
                                            <div style={{ flex: 1 }}>
                                                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                                    <h1 style={{
                                                        fontSize: '1.35rem',
                                                        fontWeight: '700',
                                                        color: '#0f172a',
                                                        margin: 0,
                                                        lineHeight: 1.2,
                                                        letterSpacing: '-0.02em'
                                                    }}>
                                                        {musteri?.adiSoyadi || t('msg.yukleniyor')}
                                                    </h1>
                                                    
                                                    <span
                                                        style={{
                                                            padding: '0.375rem 0.875rem',
                                                            borderRadius: '9999px',
                                                            fontSize: '0.8125rem',
                                                            fontWeight: '700',
                                                            background: musteri?.aktifMi ? '#dcfce7' : '#fef2f2',
                                                            color: musteri?.aktifMi ? '#15803d' : '#dc2626',
                                                            border: '1px solid',
                                                            borderColor: musteri?.aktifMi ? '#a7f3d0' : '#fecaca',
                                                            lineHeight: 1,
                                                            letterSpacing: '0.02em'
                                                        }}
                                                    >
                                                        {musteri?.aktifMi ? t('durum.aktif') : t('durum.pasif')}
                                                    </span>
                                                </div>
                                                
                                                {musteri?.firmaAdi && (
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        color: '#64748b',
                                                        fontWeight: '500',
                                                        marginTop: '0.25rem'
                                                    }}>
                                                        {musteri.firmaAdi}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                                            <button
                                                onClick={() => setShowDosyaModal(true)}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Dosya Ekle"
                                            >
                                                <FileUp size={18} />
                                            </button>
                                            <button
                                                onClick={() => setShowNotModal(true)}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Not Ekle"
                                            >
                                                <StickyNote size={18} />
                                            </button>
                                            <button
                                                onClick={handleSunumOlustur}
                                                disabled={sunumOlusturuluyor}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: sunumOlusturuluyor ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Sunum Oluştur"
                                            >
                                                <MonitorPlay size={18} />
                                            </button>
                                            <button
                                                onClick={() => navigate('/satis-sozlesmesi', { state: { selectedCustomer: musteri } })}
                                                style={{
                                                    padding: '0.625rem',
                                                    background: 'white',
                                                    border: '1.5px solid #e2e8f0',
                                                    borderRadius: '0.5rem',
                                                    color: '#64748b',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '0.375rem',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#f8fafc';
                                                    e.currentTarget.style.borderColor = '#cd6906';
                                                    e.currentTarget.style.color = '#cd6906';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = 'white';
                                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                                    e.currentTarget.style.color = '#64748b';
                                                }}
                                                title="Yeni Sözleşme"
                                            >
                                                <Plus size={18} />
                                            </button>
                                            <button
                                                onClick={() => { setActiveFormTab('temel'); setShowModal(true); if(musteri?.cariID){ axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri.cariID}`).then(r=>setIysIzinlerMd(r.data)).catch(()=>{}); } }}
                                                style={{
                                                    padding: '0.625rem 1.125rem',
                                                    background: '#cd6906',
                                                    border: 'none',
                                                    borderRadius: '0.5rem',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    fontWeight: '600',
                                                    fontSize: '0.875rem',
                                                    boxShadow: '0 2px 4px rgba(205, 105, 6, 0.2)'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#b85d05';
                                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(205, 105, 6, 0.3)';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = '#cd6906';
                                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(205, 105, 6, 0.2)';
                                                }}
                                                title={t('btn.duzenle')}
                                            >
                                                <Edit size={16} />
                                                {t('btn.duzenle')}
                                            </button>
                                            {/* İşlemler Dropdown */}
                                            {musteri.cariID !== 0 && (
                                            <div style={{ position: 'relative' }}>
                                                <button
                                                    onClick={() => setShowIslemlerMenu(o => !o)}
                                                    style={{
                                                        padding: '0.625rem 1.125rem',
                                                        background: showIslemlerMenu ? '#1a2340' : '#0f172a',
                                                        border: 'none',
                                                        borderRadius: '0.5rem',
                                                        color: 'white',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.375rem',
                                                        fontWeight: '600',
                                                        fontSize: '0.875rem',
                                                    }}
                                                    onMouseOver={(e) => { e.currentTarget.style.background = '#1e293b'; }}
                                                    onMouseOut={(e) => { e.currentTarget.style.background = showIslemlerMenu ? '#1a2340' : '#0f172a'; }}
                                                >
                                                    <i className="fas fa-bolt" style={{ fontSize: '0.75rem' }}></i>
                                                    {t('lead.islem')}
                                                    <i className={`fas fa-chevron-${showIslemlerMenu ? 'up' : 'down'}`} style={{ fontSize: '0.65rem' }}></i>
                                                </button>
                                                {showIslemlerMenu && (
                                                    <>
                                                        <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setShowIslemlerMenu(false)} />
                                                        <div style={{
                                                            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                                            background: 'white', border: '1px solid #e2e8f0',
                                                            borderRadius: '0.625rem', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                                                            minWidth: 185, zIndex: 1000, overflow: 'hidden'
                                                        }}>
                                                            {([
                                                                { label: t('musteri.tahsilatEkle'), icon: 'fa-plus', color: '#cd6906', onClick: handleTahsilatEkle },
                                                                { label: t('musteri.odemeEkle'), icon: 'fa-minus', color: '#dc2626', onClick: handleOdemeEkle },
                                                            ] as { label: string; icon: string; color: string; onClick: () => void }[]).map((item, idx, arr) => (
                                                                <button
                                                                    key={idx}
                                                                    onClick={() => { item.onClick(); setShowIslemlerMenu(false); }}
                                                                    style={{
                                                                        width: '100%', padding: '0.65rem 1rem',
                                                                        background: 'transparent', border: 'none',
                                                                        borderBottom: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                                        display: 'flex', alignItems: 'center', gap: '0.625rem',
                                                                        cursor: 'pointer', color: '#374151', fontWeight: '500',
                                                                        fontSize: '0.875rem', textAlign: 'left' as const
                                                                    }}
                                                                    onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; }}
                                                                    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
                                                                >
                                                                    <i className={`fas ${item.icon}`} style={{ color: item.color, fontSize: '0.8rem', width: 16 }}></i>
                                                                    {item.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Alt Kısım - İletişim Bilgileri */}
                                <div style={{ padding: '1.75rem 2rem' }}>
                                    <h3 style={{
                                        fontSize: '0.8125rem',
                                        fontWeight: '700',
                                        color: '#94a3b8',
                                        letterSpacing: '0.1em',
                                        marginBottom: '1.25rem',
                                        textTransform: 'uppercase'
                                        }}>{t('musteri.iletisimBilgileri')}</h3>
                                    <Row>
                                        {/* Telefon 1 - 1/4 */}
                                        <Col md={3}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Phone size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>TELEFON 1</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600', marginBottom: '0.5rem' }}>
                                                        {formatPhoneNumber(musteri.telefon1)}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                                                        <a href={`tel:+90${musteri.telefon1}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                            <Phone size={12} /> Ara
                                                        </a>
                                                        <a href={`https://wa.me/90${musteri.telefon1}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#25d366', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                            <i className="fab fa-whatsapp" style={{ fontSize: 12 }}></i> WhatsApp
                                                        </a>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowSmsModal(true)}
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#eef2ff', color: '#6366f1', fontWeight: 600, fontSize: '0.75rem', border: '1px solid #c7d2fe', cursor: 'pointer' }}
                                                        >
                                                            <MessageCircle size={12} /> {t('lead.smsGonder')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Telefon 2 */}
                                            {musteri.telefon2 && (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1.25rem' }}>
                                                    <div style={{
                                                        width: '2rem',
                                                        height: '2rem',
                                                        borderRadius: '0.5rem',
                                                        background: '#fff8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <Phone size={16} style={{ color: '#cd6906' }} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>TELEFON 2</div>
                                                        <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600', marginBottom: '0.5rem' }}>
                                                            {formatPhoneNumber(musteri.telefon2)}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                                                            <a href={`tel:+90${musteri.telefon2}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                                <Phone size={12} /> Ara
                                                            </a>
                                                            <a href={`https://wa.me/90${musteri.telefon2}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#f0fdf4', color: '#25d366', fontWeight: 600, fontSize: '0.75rem', textDecoration: 'none', border: '1px solid #bbf7d0' }}>
                                                                <i className="fab fa-whatsapp" style={{ fontSize: 12 }}></i> WhatsApp
                                                            </a>
                                                            <button
                                                                type="button"
                                                                onClick={() => setShowSmsModal(true)}
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '4px 10px', borderRadius: '6px', background: '#eef2ff', color: '#6366f1', fontWeight: 600, fontSize: '0.75rem', border: '1px solid #c7d2fe', cursor: 'pointer' }}
                                                            >
                                                                <MessageCircle size={12} /> {t('lead.smsGonder')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                        
                                        {/* E-Posta - 1/4 */}
                                        <Col md={3}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <Mail size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>E-POSTA</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600', wordBreak: 'break-word' }}>
                                                        {musteri.ePosta || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        
                                        {/* Adres - 1/2 */}
                                        <Col md={6}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{
                                                    width: '2rem',
                                                    height: '2rem',
                                                    borderRadius: '0.5rem',
                                                    background: '#fff8f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    <MapPin size={16} style={{ color: '#cd6906' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>{t('label.adres').toUpperCase()}</div>
                                                    <div style={{ fontSize: '0.9375rem', color: '#0f172a', lineHeight: '1.6', fontWeight: '500' }}>
                                                        {musteri.adres || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Firma Adı */}
                                            {musteri.firmaAdi && (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1.25rem' }}>
                                                    <div style={{
                                                        width: '2rem',
                                                        height: '2rem',
                                                        borderRadius: '0.5rem',
                                                        background: '#fff8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <Building2 size={16} style={{ color: '#cd6906' }} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>{t('musteri.firmaAdi').toUpperCase()}</div>
                                                        <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600' }}>
                                                            {musteri.firmaAdi}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Kaynak */}
                                            {musteri.kaynak && (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1.25rem' }}>
                                                    <div style={{
                                                        width: '2rem',
                                                        height: '2rem',
                                                        borderRadius: '0.5rem',
                                                        background: '#fff8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <i className="fas fa-megaphone" style={{ fontSize: 14, color: '#cd6906' }}></i>
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '0.375rem', fontWeight: '600', letterSpacing: '0.05em' }}>{t('musteri.kaynak').toUpperCase()}</div>
                                                        <div style={{ fontSize: '0.9375rem', color: '#0f172a', fontWeight: '600' }}>
                                                            {musteri.kaynak}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                    
                    {/* Ana Sekme Navigasyonu */}
                    <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', gap: 0 }}>
                            {([
                                { key: 'sozlesmeler' as const, label: t('musteri.sozlesmeler'), icon: 'fa-file-alt' },
                                { key: 'finans' as const, label: t('musteri.finansCari'), icon: 'fa-chart-bar' },
                                { key: 'sunumlar' as const, label: t('cari.sunumlar', 'Sunumlar'), icon: 'fa-desktop' },
                                { key: 'aktiviteler' as const, label: t('musteri.aktiviteler'), icon: 'fa-clock' },
                                { key: 'dosyalar' as const, label: t('musteri.dosyalar'), icon: 'fa-paperclip' },
                                { key: 'smsler' as const, label: t('lead.smsGecmisi'), icon: 'fa-comment-sms' },
                            ]).map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveMainTab(tab.key)}
                                    style={{
                                        padding: '0.75rem 1.25rem',
                                        background: 'none',
                                        border: 'none',
                                        borderBottom: activeMainTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
                                        color: activeMainTab === tab.key ? '#2563eb' : '#6b7280',
                                        fontWeight: activeMainTab === tab.key ? '600' : '500',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.375rem',
                                        marginBottom: '-1px',
                                        whiteSpace: 'nowrap' as const,
                                    }}
                                    onMouseOver={e => { if (activeMainTab !== tab.key) e.currentTarget.style.color = '#374151'; }}
                                    onMouseOut={e => { if (activeMainTab !== tab.key) e.currentTarget.style.color = '#6b7280'; }}
                                >
                                    <i className={`fas ${tab.icon}`} style={{ fontSize: '0.75rem' }}></i>
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* SOZLESMELER TAB */}
                    {activeMainTab === 'sozlesmeler' && (<>
                    {/* Sözleşmeler - Tam Genişlik */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <div style={{
                                background: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '1rem',
                                overflow: 'hidden',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
                            }}>
                                <div style={{
                                    padding: '1.25rem 1.5rem',
                                    borderBottom: '1px solid #f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}>
                                    <h3 style={{
                                        fontSize: '1.125rem',
                                        fontWeight: '700',
                                        color: '#0f172a',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FileText size={20} style={{ color: '#cd6906' }} />
                                        {t('musteri.iliskiGecmisiSozlesmeler')}
                                        <span style={{
                                            background: '#f1f5f9',
                                            color: '#475569',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            padding: '2px 10px',
                                            borderRadius: '10px',
                                            marginLeft: '0.25rem'
                                        }}>{sozlesmeler.length}</span>
                                    </h3>
                                    {sozlesmeler.length > 0 && (
                                        <div style={{ fontSize: '0.875rem', color: '#475569', fontWeight: 600 }}>
                                            Toplam: ₺{new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(sozlesmeler.reduce((sum, s) => sum + s.sozlesmeFiyati, 0))}
                                        </div>
                                    )}
                                </div>

                                {/* Tablo Başlık */}
                                {sozlesmeler.length > 0 && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '10px 20px 10px 24px',
                                        borderBottom: '1px solid #f1f5f9',
                                        fontSize: '0.6875rem',
                                        fontWeight: '600',
                                        color: '#94a3b8',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase' as const,
                                        gap: 0
                                    }}>
                                        <div style={{ flex: '0 0 220px' }}>{t('musteri.projeBlok')}</div>
                                        <div style={{ flex: '0 0 100px' }}>{t('musteri.daire')}</div>
                                        <div style={{ flex: '0 0 130px' }}>{t('label.durum')}</div>
                                        <div style={{ flex: '0 0 130px' }}>{t('musteri.sozlesmeTarihi')}</div>
                                        <div style={{ flex: 1 }}>{t('label.fiyat')}</div>
                                        <div style={{ flex: '0 0 100px', textAlign: 'center' }}>{t('lead.islem')}</div>
                                    </div>
                                )}

                                <div>
                                    {sozlesmeler.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '4rem 2rem', 
                                            color: '#94a3b8',
                                            fontSize: '0.9375rem'
                                        }}>
                                            <FileText size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                                            <div style={{ fontWeight: '500' }}>
                                                {t('musteri.aktifSozlesmeYok')}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '0.5rem' }}>
                                            {sozlesmeler.map((sozlesme) => {
                                                const durumConfig: Record<string, { border: string; bg: string; badgeBg: string; badgeColor: string }> = {
                                                    'Sözleşme':   { border: '#3b82f6', bg: '#eff6ff', badgeBg: '#dbeafe', badgeColor: '#1d4ed8' },
                                                    'Rezerve':    { border: '#f59e0b', bg: '#fffbeb', badgeBg: '#fef9c3', badgeColor: '#ca8a04' },
                                                    'ArsaSahibi': { border: '#8b5cf6', bg: '#f5f3ff', badgeBg: '#ede9fe', badgeColor: '#6d28d9' },
                                                    'Taşeron':    { border: '#0ea5e9', bg: '#f0f9ff', badgeBg: '#e0f2fe', badgeColor: '#0369a1' },
                                                    'Aktif':      { border: '#10b981', bg: '#ecfdf5', badgeBg: '#dcfce7', badgeColor: '#15803d' },
                                                };
                                                const cfg = durumConfig[sozlesme.sozlesmeDurumu] || { border: '#94a3b8', bg: '#f8fafc', badgeBg: '#f1f5f9', badgeColor: '#475569' };

                                                return (
                                                    <div 
                                                        key={sozlesme.sozlesmeID} 
                                                        style={{
                                                            display: 'flex',
                                                            borderRadius: '14px',
                                                            border: '1.5px solid #f1f5f9',
                                                            background: '#fff',
                                                            marginBottom: '0.5rem',
                                                            overflow: 'hidden',
                                                            transition: 'all 0.18s',
                                                            cursor: 'pointer'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.currentTarget.style.background = '#fef9f4';
                                                            e.currentTarget.style.boxShadow = '0 4px 18px rgba(15,23,42,0.10)';
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.currentTarget.style.background = '#fff';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                            e.currentTarget.style.transform = 'none';
                                                        }}
                                                        onClick={() => navigate(`/sozlesme-detay/${sozlesme.sozlesmeTakipNo}`)}
                                                    >
                                                        {/* Sol Renkli Accent */}
                                                        <div style={{
                                                            width: '4px',
                                                            borderRadius: '14px 0 0 14px',
                                                            flexShrink: 0,
                                                            background: cfg.border
                                                        }} />

                                                        {/* İçerik */}
                                                        <div style={{
                                                            padding: '16px 20px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            flex: 1,
                                                            gap: 0,
                                                            minWidth: 0
                                                        }}>
                                                            {/* Proje / Blok */}
                                                            <div style={{ flex: '0 0 220px', overflow: 'hidden' }}>
                                                                <div style={{
                                                                    fontSize: '13px',
                                                                    fontWeight: 600,
                                                                    color: '#0f172a',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {sozlesme.daire?.blok?.proje?.projeAdi || '—'}
                                                                </div>
                                                                {sozlesme.daire?.blok?.blokAdi && (
                                                                    <div style={{
                                                                        fontSize: '12px',
                                                                        color: '#64748b',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        marginTop: '2px'
                                                                    }}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#94a3b8" width="11" height="11">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                                                                        </svg>
                                                                        {sozlesme.daire.blok.blokAdi}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Daire */}
                                                            <div style={{ flex: '0 0 100px' }}>
                                                                <span style={{
                                                                    background: '#f1f5f9',
                                                                    color: '#475569',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    padding: '5px 12px',
                                                                    borderRadius: '8px',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {sozlesme.daire?.daireNo ? `Daire ${sozlesme.daire.daireNo}` : '—'}
                                                                </span>
                                                            </div>

                                                            {/* Durum */}
                                                            <div style={{ flex: '0 0 130px' }}>
                                                                <span style={{
                                                                    background: cfg.badgeBg,
                                                                    color: cfg.badgeColor,
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    padding: '4px 12px',
                                                                    borderRadius: '20px',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px'
                                                                }}>
                                                                    <span style={{
                                                                        width: '6px',
                                                                        height: '6px',
                                                                        borderRadius: '50%',
                                                                        background: cfg.border,
                                                                        flexShrink: 0
                                                                    }} />
                                                                    {sozlesme.sozlesmeDurumu === 'ArsaSahibi' ? 'Arsa Sahibi' : sozlesme.sozlesmeDurumu}
                                                                </span>
                                                            </div>

                                                            {/* Tarih */}
                                                            <div style={{ flex: '0 0 130px' }}>
                                                                <div style={{
                                                                    fontSize: '11px',
                                                                    color: '#94a3b8',
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase' as const,
                                                                    letterSpacing: '0.3px'
                                                                }}>{t('musteri.tarihUst')}</div>
                                                                <div style={{
                                                                    fontSize: '13px',
                                                                    color: '#334155',
                                                                    fontWeight: 600
                                                                }}>
                                                                    {new Date(sozlesme.sozlesmeTarihi).toLocaleDateString('tr-TR')}
                                                                </div>
                                                            </div>

                                                            {/* Fiyat */}
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '11px',
                                                                    color: '#94a3b8',
                                                                    fontWeight: 500,
                                                                    textTransform: 'uppercase' as const,
                                                                    letterSpacing: '0.3px'
                                                                }}>{t('musteri.sozlesmeFiyatiUst')}</div>
                                                                <div style={{
                                                                    fontSize: '15px',
                                                                    color: '#0f172a',
                                                                    fontWeight: 600
                                                                }}>
                                                                    ₺{new Intl.NumberFormat('tr-TR', {
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2
                                                                    }).format(sozlesme.sozlesmeFiyati)}
                                                                </div>
                                                            </div>

                                                            {/* İşlemler */}
                                                            <div style={{ flex: '0 0 100px', textAlign: 'center' }}>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/sozlesme-detay/${sozlesme.sozlesmeTakipNo}`);
                                                                    }}
                                                                    style={{
                                                                        padding: '6px 12px',
                                                                        background: 'transparent',
                                                                        border: '1px solid #cd6906',
                                                                        borderRadius: '6px',
                                                                        color: '#cd6906',
                                                                        fontSize: '13px',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.15s',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        gap: '6px'
                                                                    }}
                                                                    onMouseOver={(e) => {
                                                                        e.currentTarget.style.background = '#cd6906';
                                                                        e.currentTarget.style.color = '#fff';
                                                                    }}
                                                                    onMouseOut={(e) => {
                                                                        e.currentTarget.style.background = 'transparent';
                                                                        e.currentTarget.style.color = '#cd6906';
                                                                    }}
                                                                >
                                                                    <Eye size={12} />
                                                                    {t('musteri.detaylar')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    </>)}

                    {/* FİNANS & CARİ TAB */}
                    {activeMainTab === 'finans' && (() => {
                        // Sadece kesinleşmiş sözleşmeler cari hesaba dahil edilir (Rezerve ve Satışta hariç)
                        const cariSozlesmeler = sozlesmeler.filter(sz => sz.sozlesmeDurumu === 'Sözleşme' || sz.sozlesmeDurumu === 'Taşeron' || sz.sozlesmeDurumu === 'ArsaSahibi');
                        // Borç/Alacak hesapları
                        // sozlesmeOdemesi=true ise tur değil odemeYonu'na göre belirlenir
                        const sozlesmeBorc = cariSozlesmeler.reduce((s, sz) => s + (sz.sozlesmeFiyati || 0), 0);
                        const genelToplamBorc = sozlesmeBorc +
                            odemeler.filter(o => !isSozlesmeGeliri(o) && (
                                (!o.sozlesmeOdemesi && o.islemTipi === 'Gelir') ||
                                (o.sozlesmeOdemesi && o.odemeYonu === 'Odeme' && o.odemeDurumu === 'Odendi') ||
                                (!o.sozlesmeOdemesi && o.islemTipi === 'Odeme' && o.odemeDurumu === 'Odendi')
                            )).reduce((s, o) => s + o.miktar, 0);
                        const genelToplamAlacak =
                            odemeler.filter(o => !isSozlesmeGeliri(o) && (
                                o.islemTipi === 'Gider' ||
                                (!o.sozlesmeOdemesi && o.islemTipi === 'Tahsilat' && o.odemeDurumu === 'Odendi') ||
                                (o.sozlesmeOdemesi && o.odemeYonu === 'Tahsilat' && o.odemeDurumu === 'Odendi')
                            )).reduce((s, o) => s + o.miktar, 0);
                        const netBakiye = genelToplamBorc - genelToplamAlacak;

                        // Ekstre satırları
                        const expandedRows: Array<{
                            key: string; type: 'sozlesme' | 'fatura' | 'makbuz';
                            date: string; label: string; subLabel?: string;
                            rowBorc: number | null; rowAlacak: number | null;
                            durum?: string; kasaAdi?: string; orijinalOdeme?: Odeme;
                        }> = [];

                        cariSozlesmeler.forEach(sz => {
                            const projeAdi = sz.daire?.blok?.proje?.projeAdi || '';
                            const daireNo = sz.daire?.daireNo || '';
                            expandedRows.push({
                                key: `sozlesme-${sz.sozlesmeID}`, type: 'sozlesme',
                                date: sz.sozlesmeTarihi, label: 'Sat\u0131\u015f S\u00f6zle\u015fmesi',
                                subLabel: `${projeAdi}${daireNo ? ' - Daire ' + daireNo : ''}`,
                                rowBorc: sz.sozlesmeFiyati, rowAlacak: null, durum: sz.sozlesmeDurumu,
                            });
                        });

                        odemeler.filter(o => !isSozlesmeGeliri(o)).forEach(o => {
                            const tur = o.islemTipi || '';
                            // Bekliyor olan kayıtlar ekstre'de gizlenir
                            if (o.odemeDurumu === 'Bekliyor') return;

                            // Sözleşme ödemeleri: odemeYonu'na göre Borç/Alacak
                            if (o.sozlesmeOdemesi) {
                                if (o.odemeYonu === 'Tahsilat' && o.odemeDurumu === 'Odendi') {
                                    // Sözleşme tahsilatı → Alacak
                                    expandedRows.push({
                                        key: `soz-tahsilat-${o.odemeID}`, type: 'makbuz',
                                        date: o.tahsilatTarihi || o.islemTarihi, label: 'Tahsilat',
                                        subLabel: o.aciklama || '',
                                        rowBorc: null, rowAlacak: o.miktar,
                                        durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                    });
                                } else if (o.odemeYonu === 'Odeme' && o.odemeDurumu === 'Odendi') {
                                    // Sözleşme ödemesi → Borç
                                    expandedRows.push({
                                        key: `soz-odeme-${o.odemeID}`, type: 'fatura',
                                        date: o.tahsilatTarihi || o.islemTarihi, label: 'Ödeme',
                                        subLabel: o.aciklama || '',
                                        rowBorc: o.miktar, rowAlacak: null,
                                        durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                    });
                                }
                                return;
                            }

                            // Normal ödemeler: tur'a göre
                            if (tur === 'Gider' && o.odemeDurumu === 'Odendi') {
                                // Gider → Alacak
                                expandedRows.push({
                                    key: `gider-${o.odemeID}`, type: 'fatura',
                                    date: o.islemTarihi, label: o.aciklama || o.odemeTuru,
                                    rowBorc: null, rowAlacak: o.miktar,
                                    durum: o.odemeDurumu, kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            } else if (tur === 'Gelir' && o.odemeDurumu === 'Odendi') {
                                // Gelir → Borç
                                expandedRows.push({
                                    key: `gelir-${o.odemeID}`, type: 'fatura',
                                    date: o.islemTarihi, label: o.aciklama || o.odemeTuru,
                                    rowBorc: o.miktar, rowAlacak: null,
                                    durum: o.odemeDurumu, kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            } else if (tur === 'Tahsilat' && o.odemeDurumu === 'Odendi') {
                                // Tahsilat + Odendi → Alacak
                                expandedRows.push({
                                    key: `tahsilat-${o.odemeID}`, type: 'makbuz',
                                    date: o.tahsilatTarihi || o.islemTarihi, label: 'Tahsilat',
                                    subLabel: o.aciklama || '',
                                    rowBorc: null, rowAlacak: o.miktar,
                                    durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            } else if (tur === 'Odeme' && o.odemeDurumu === 'Odendi') {
                                // Ödeme + Odendi → Borç
                                expandedRows.push({
                                    key: `odeme-${o.odemeID}`, type: 'fatura',
                                    date: o.tahsilatTarihi || o.islemTarihi, label: 'Ödeme',
                                    subLabel: o.aciklama || '',
                                    rowBorc: o.miktar, rowAlacak: null,
                                    durum: 'Odendi', kasaAdi: o.kasa?.kasaAdi, orijinalOdeme: o,
                                });
                            }
                        });

                        expandedRows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                        // Bakiye hesapla
                        let runningBakiye = 0;
                        const rowsWithBakiye = expandedRows.map(r => {
                            runningBakiye += (r.rowBorc || 0) - (r.rowAlacak || 0);
                            return { ...r, bakiye: runningBakiye };
                        });

                        // Filtreli ekstre
                        const filteredRows = rowsWithBakiye.filter(r => {
                            if (hareketFilters.vadeTarihiBaslangic && r.date < hareketFilters.vadeTarihiBaslangic) return false;
                            if (hareketFilters.vadeTarihiBitis && r.date > hareketFilters.vadeTarihiBitis) return false;
                            if (hareketFilters.odemeDurumu && r.durum !== hareketFilters.odemeDurumu) return false;
                            return true;
                        });

                        // Plan tab - tüm Tahsilat/Ödeme kayıtları (Bekliyor dahil)
                        const taksitler = odemeler.filter(o => {
                            if (!isSozlesmeGeliri(o) && (o.islemTipi === 'Tahsilat' || o.islemTipi === 'Odeme')) {
                                if (odemePlaniSozlesmeFiltre) return o.islemTakipNo === odemePlaniSozlesmeFiltre || (o.sozlesme?.sozlesmeTakipNo === odemePlaniSozlesmeFiltre);
                                return true;
                            }
                            return false;
                        }).sort((a, b) => {
                            if (!a.vadeTarihi && !b.vadeTarihi) return 0;
                            if (!a.vadeTarihi) return 1;
                            if (!b.vadeTarihi) return -1;
                            return new Date(a.vadeTarihi).getTime() - new Date(b.vadeTarihi).getTime();
                        });
                        const bekleyenPlanTaksitleri = sozlesmePlanTaksitleri.filter((t: any) =>
                            t.odemeDurumu === 'Bekliyor' && t.aktifMi !== false &&
                            (!odemePlaniSozlesmeFiltre || t.sozlesmeTakipNo === odemePlaniSozlesmeFiltre)
                        );
                        const toplamPlanTaksitMiktar = bekleyenPlanTaksitleri.reduce((s: number, t: any) => s + t.miktar, 0);
                        const toplamPlanlanan = taksitler.reduce((s, o) => s + o.miktar, 0) + toplamPlanTaksitMiktar;
                        const toplamTahsil = taksitler.filter(o => o.odemeDurumu === 'Odendi').reduce((s, o) => s + o.miktar, 0);
                        const toplamKalan = toplamPlanlanan - toplamTahsil;

                        return (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                        {/* Header */}
                                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                                            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <CreditCard size={20} style={{ color: '#cd6906' }} />
                                                {t('musteri.cariHesap')}
                                            </h3>
                                            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{t('musteri.toplamBorc')}</div>
                                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#dc2626' }}>₺{formatCurrency(genelToplamBorc)}</div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{t('musteri.toplamAlacak')}</div>
                                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#16a34a' }}>₺{formatCurrency(genelToplamAlacak)}</div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{t('musteri.netBakiye')}</div>
                                                    <div style={{ fontSize: 15, fontWeight: 700, color: netBakiye > 0 ? '#dc2626' : '#16a34a' }}>₺{formatCurrency(Math.abs(netBakiye))}</div>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Tab bar */}
                                        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
                                            {(['ekstre', 'plan'] as const).map(tab => (
                                                <button key={tab} onClick={() => setActiveHareketTab(tab)}
                                                    style={{ padding: '0.75rem 1.5rem', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeHareketTab === tab ? 700 : 400, color: activeHareketTab === tab ? '#cd6906' : '#64748b', borderBottom: activeHareketTab === tab ? '2px solid #cd6906' : '2px solid transparent', marginBottom: -1, fontSize: 14 }}>
                                                    {tab === 'ekstre' ? t('musteri.cariEkstre') : t('musteri.odemePlani')}
                                                </button>
                                            ))}
                                        </div>

                                        {/* EKSTRE TAB */}
                                        {activeHareketTab === 'ekstre' && (
                                            <div style={{ padding: '1rem 1.5rem' }}>
                                                {/* Filtreler */}
                                                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                    <div>
                                                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>{t('kasa.baslangic')}</label>
                                                        <TrDateInput value={hareketFilters.vadeTarihiBaslangic}
                                                            onChange={e => setHareketFilters(f => ({ ...f, vadeTarihiBaslangic: e.target.value }))}
                                                            style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 13 }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>{t('kasa.bitis')}</label>
                                                        <TrDateInput value={hareketFilters.vadeTarihiBitis}
                                                            onChange={e => setHareketFilters(f => ({ ...f, vadeTarihiBitis: e.target.value }))}
                                                            style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 13 }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>{t('label.durum')}</label>
                                                        <select value={hareketFilters.odemeDurumu}
                                                            onChange={e => setHareketFilters(f => ({ ...f, odemeDurumu: e.target.value }))}
                                                            style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 13 }}>
                                                            <option value="">{t('label.tumu')}</option>
                                                            <option value="Odendi">{t('musteri.odendi')}</option>
                                                            <option value="Bekliyor">{t('lead.bekliyor')}</option>
                                                        </select>
                                                    </div>
                                                    {(hareketFilters.vadeTarihiBaslangic || hareketFilters.vadeTarihiBitis || hareketFilters.odemeDurumu) && (
                                                        <button onClick={() => setHareketFilters({ vadeTarihiBaslangic: '', vadeTarihiBitis: '', odemeTuru: '', odemeDurumu: '' })}
                                                            style={{ padding: '4px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', fontSize: 12, color: '#64748b' }}>
                                                            {t('btn.temizle')}
                                                        </button>
                                                    )}
                                                </div>
                                                {/* Tablo */}
                                                <div style={{ overflowX: 'auto' }}>
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                        <thead>
                                                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>{t('label.tarih')}</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{t('musteri.aciklama')}</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{t('label.tur')}</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>{t('musteri.borc')}</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>{t('musteri.alacak')}</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>{t('musteri.bakiye')}</th>
                                                                <th style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>{t('lead.islem')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredRows.length === 0 ? (
                                                                <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>{t('musteri.kayitBulunamadi')}</td></tr>
                                                            ) : filteredRows.map((r, idx) => (
                                                                <tr key={r.key} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#475569' }}>{r.date ? toTRDate(r.date.slice(0, 10)) : '—'}</td>
                                                                    <td style={{ padding: '8px 12px' }}>
                                                                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.label}</div>
                                                                        {r.subLabel && <div style={{ fontSize: 11, color: '#64748b' }}>{r.subLabel}</div>}
                                                                        {r.kasaAdi && <div style={{ fontSize: 11, color: '#64748b' }}>{r.kasaAdi}</div>}
                                                                    </td>
                                                                    <td style={{ padding: '8px 12px' }}>
                                                                        {r.type === 'sozlesme' ? (
                                                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed', fontWeight: 600, border: '1px solid #ddd6fe' }}>{t('musteri.sozlesme')}</span>
                                                                        ) : (() => {
                                                                            const tur = r.orijinalOdeme?.islemTipi || '';
                                                                            if (tur === 'Gelir') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dcfce7', color: '#16a34a', fontWeight: 600, border: '1px solid #bbf7d0' }}>{t('musteri.gelir')}</span>;
                                                                            if (tur === 'Gider') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fee2e2', color: '#dc2626', fontWeight: 600, border: '1px solid #fecaca' }}>{t('musteri.gider')}</span>;
                                                                            if (tur === 'Tahsilat') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#2563eb', fontWeight: 600, border: '1px solid #bfdbfe' }}>{t('musteri.tahsilat')}</span>;
                                                                            if (tur === 'Odeme') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#b45309', fontWeight: 600, border: '1px solid #fde68a' }}>{t('musteri.odeme')}</span>;
                                                                            return null;
                                                                        })()}
                                                                    </td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>{r.rowBorc != null ? `₺${formatCurrency(r.rowBorc)}` : ''}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>{r.rowAlacak != null ? `₺${formatCurrency(r.rowAlacak)}` : ''}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: r.bakiye > 0 ? '#dc2626' : '#16a34a' }}>₺{formatCurrency(Math.abs(r.bakiye))}</td>
                                                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                        {r.orijinalOdeme && r.orijinalOdeme.odemeDurumu === 'Bekliyor' && (
                                                                            <button onClick={() => handleOdemeEditAc(r.orijinalOdeme!)}
                                                                                style={{ padding: '3px 10px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                {t('btn.duzenle')}
                                                                            </button>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot>
                                                            <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                                                                <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 700, color: '#0f172a' }}>{t('musteri.toplam')}</td>
                                                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>₺{formatCurrency(genelToplamBorc)}</td>
                                                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>₺{formatCurrency(genelToplamAlacak)}</td>
                                                                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: netBakiye > 0 ? '#dc2626' : '#16a34a' }}>₺{formatCurrency(Math.abs(netBakiye))}</td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* ÖDEME PLANI TAB */}
                                        {activeHareketTab === 'plan' && (
                                            <div style={{ padding: '1rem 1.5rem' }}>
                                                {/* Sözleşme Filtresi */}
                                                {sozlesmeler.filter(s => s.sozlesmeDurumu === 'Sözleşme').length > 1 && (
                                                    <div style={{ marginBottom: '1rem' }}>
                                                        <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4, display: 'block' }}>{t('musteri.sozlesme')}</label>
                                                        <select
                                                            value={odemePlaniSozlesmeFiltre}
                                                            onChange={e => { setOdemePlaniSozlesmeFiltre(e.target.value); setSelectedTaksitler(new Set()); }}
                                                            style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0f172a', background: '#fff', minWidth: 280, cursor: 'pointer' }}
                                                        >
                                                            <option value="">— Tüm sözleşmeler —</option>
                                                            {sozlesmeler.filter(s => s.sozlesmeDurumu === 'Sözleşme').map(s => {
                                                                const proje = s.daire?.blok?.proje?.projeAdi || '';
                                                                const blok = s.daire?.blok?.blokAdi || '';
                                                                const daire = s.daire?.daireNo || '';
                                                                let label = '';
                                                                if (proje) label += proje;
                                                                if (blok) label += (label ? ' · ' : '') + blok;
                                                                if (daire) label += (label ? ' · ' : '') + 'Daire ' + daire;
                                                                if (!label) label = s.sozlesmeTakipNo;
                                                                if (s.sozlesmeFiyati) label += ` · ${s.sozlesmeFiyati.toLocaleString('tr-TR')} ₺`;
                                                                return <option key={s.sozlesmeID} value={s.sozlesmeTakipNo}>{label}</option>;
                                                            })}
                                                        </select>
                                                    </div>
                                                )}
                                                {/* KPI */}
                                                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                                    {[
                                                        { label: t('musteri.toplamPlanlanan'), value: toplamPlanlanan, color: '#3b82f6' },
                                                        { label: t('musteri.tahsilEdilen'), value: toplamTahsil, color: '#16a34a' },
                                                        { label: t('musteri.kalan'), value: toplamKalan, color: '#dc2626' },
                                                    ].map(kpi => (
                                                        <div key={kpi.label} style={{ flex: 1, minWidth: 160, background: 'white', border: '1px solid #e2e8f0', borderLeft: `4px solid ${kpi.color}`, borderRadius: '0.75rem', padding: '1rem 1.25rem' }}>
                                                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{kpi.label}</div>
                                                            <div style={{ fontSize: 18, fontWeight: 700, color: kpi.color }}>₺{formatCurrency(kpi.value)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {/* Toolbar */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                        {selectedTaksitler.size > 0 && (
                                                            <button onClick={handleTopluSilTaksit} disabled={taksitTopluSilLoading}
                                                                style={{ padding: '6px 14px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                                                                {taksitTopluSilLoading ? t('musteri.siliniyor') : t('musteri.taksitSil', { adet: selectedTaksitler.size })}
                                                            </button>
                                                        )}
                                                    </div>
                                                    <button onClick={handleShowTopluPlanlaModal}
                                                        style={{ padding: '8px 18px', background: '#cd6906', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                                                        {t('musteri.topluOdemePlanla')}
                                                    </button>
                                                </div>
                                                {/* Taksit tablosu */}
                                                {taksitler.length === 0 && bekleyenPlanTaksitleri.length === 0 ? (
                                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>{t('musteri.odemePlaniYok')}</div>
                                                ) : (
                                                    <div style={{ overflowX: 'auto' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                            <thead>
                                                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                                    <th style={{ padding: '8px 12px', width: 32 }}>
                                                                        <input type="checkbox"
                                                                            checked={selectedTaksitler.size === taksitler.filter(item => item.odemeDurumu !== 'Odendi').length && taksitler.some(item => item.odemeDurumu !== 'Odendi')}
                                                                            onChange={e => {
                                                                                if (e.target.checked) setSelectedTaksitler(new Set(taksitler.filter(item => item.odemeDurumu !== 'Odendi').map(item => item.odemeID)));
                                                                                else setSelectedTaksitler(new Set());
                                                                            }} />
                                                                    </th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{t('musteri.aciklama')}</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{t('musteri.vadeTarihi')}</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>{t('musteri.tutar')}</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{t('label.tur')}</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{t('label.durum')}</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>{t('kasa.kasa')}</th>
                                                                    <th style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>{t('lead.islem')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {taksitler.map((odeme, idx) => (
                                                                    <tr key={odeme.odemeID} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                        <td style={{ padding: '8px 12px' }}>
                                                                            {odeme.odemeDurumu !== 'Odendi' && (
                                                                                <input type="checkbox" checked={selectedTaksitler.has(odeme.odemeID)}
                                                                                    onChange={e => {
                                                                                        const s = new Set(selectedTaksitler);
                                                                                        e.target.checked ? s.add(odeme.odemeID) : s.delete(odeme.odemeID);
                                                                                        setSelectedTaksitler(s);
                                                                                    }} />
                                                                            )}
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px', color: '#0f172a', fontWeight: 500 }}>{odeme.aciklama}</td>
                                                                        <td style={{ padding: '8px 12px', color: '#475569' }}>{odeme.vadeTarihi ? toTRDate(odeme.vadeTarihi.slice(0, 10)) : '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#334155' }}>₺{formatCurrency(odeme.miktar)}</td>
                                                                        <td style={{ padding: '8px 12px' }}>
                                                                            {(() => {
                                                                                const tur = odeme.islemTipi || '';
                                                                                if (tur === 'Gelir') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dcfce7', color: '#16a34a', fontWeight: 600, border: '1px solid #bbf7d0' }}>{t('musteri.gelir')}</span>;
                                                                                if (tur === 'Gider') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fee2e2', color: '#dc2626', fontWeight: 600, border: '1px solid #fecaca' }}>{t('musteri.gider')}</span>;
                                                                                if (tur === 'Tahsilat') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#2563eb', fontWeight: 600, border: '1px solid #bfdbfe' }}>{t('musteri.tahsilat')}</span>;
                                                                                if (tur === 'Odeme') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#b45309', fontWeight: 600, border: '1px solid #fde68a' }}>{t('musteri.odeme')}</span>;
                                                                                return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>;
                                                                            })()}
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px' }}>
                                                                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: odeme.odemeDurumu === 'Odendi' ? '#dcfce7' : '#fef3c7', color: odeme.odemeDurumu === 'Odendi' ? '#16a34a' : '#b45309', fontWeight: 600 }}>
                                                                                {odeme.odemeDurumu === 'Odendi' ? t('musteri.odendi') : t('lead.bekliyor')}
                                                                            </span>
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 12 }}>{odeme.kasa?.kasaAdi || '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                                                {odeme.odemeDurumu !== 'Odendi' && (
                                                                                    <button onClick={() => handleOdemeEditAc(odeme)}
                                                                                        style={{ padding: '4px 10px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                        {t('btn.duzenle')}
                                                                                    </button>
                                                                                )}
                                                                                {odeme.odemeDurumu !== 'Odendi' && (
                                                                                    <button onClick={() => handleOdemeYap(odeme)}
                                                                                        style={{ padding: '4px 10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                        {odeme.islemTipi === 'Odeme' ? t('musteri.odemeYap') : t('musteri.tahsilatYap')}
                                                                                    </button>
                                                                                )}
                                                                                {odeme.odemeDurumu === 'Odendi' && (
                                                                                    <button onClick={() => handleTahsilatGeriAl(odeme)}
                                                                                        style={{ padding: '4px 10px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                        {odeme.islemTipi === 'Odeme' ? t('musteri.odemeGeriAl') : t('musteri.tahsilatGeriAl')}
                                                                                    </button>
                                                                                )}
                                                                                <button onClick={() => handleDeleteTaksit(odeme)}
                                                                                    style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                                                                                    {t('btn.sil')}
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                                {bekleyenPlanTaksitleri.map((planTaksit: any, idx: number) => (
                                                                    <tr key={`pt-${planTaksit.taksitID}`} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                        <td style={{ padding: '8px 12px' }}></td>
                                                                        <td style={{ padding: '8px 12px', color: '#0f172a', fontWeight: 500 }}>
                                                                            {planTaksit.aciklama}
                                                                            <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 12, background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>{t('musteri.sozlesmePlani')}</span>
                                                                        </td>
                                                                        <td style={{ padding: '8px 12px', color: '#475569' }}>{planTaksit.vadeTarihi ? toTRDate(planTaksit.vadeTarihi.slice(0, 10)) : '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#334155' }}>₺{formatCurrency(planTaksit.miktar)}</td>
                                                                        <td style={{ padding: '8px 12px' }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#2563eb', fontWeight: 600, border: '1px solid #bfdbfe' }}>{t('musteri.tahsilat')}</span></td>
                                                                        <td style={{ padding: '8px 12px' }}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#fef3c7', color: '#b45309', fontWeight: 600 }}>{t('lead.bekliyor')}</span></td>
                                                                        <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 12 }}>{planTaksit.kasaAdi || '—'}</td>
                                                                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                                                <button onClick={() => handlePlanTaksitTahsilatYap(planTaksit)}
                                                                                    style={{ padding: '4px 10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                                                                                    {t('musteri.tahsilatYap')}
                                                                                </button>
                                                                                <button onClick={() => handleDeletePlanTaksit(planTaksit)}
                                                                                    style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                                                                                    {t('btn.sil')}
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        );
                    })()}

                    {/* Kiralık Sözleşmeler & Emlak Sunumları */}
                    {activeMainTab === 'sozlesmeler' && (relatedRecords.kiraSozlesmeleri.length > 0 || relatedRecords.emlakSunumlari.length > 0) && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <div style={{
                                    background: 'white',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '1rem',
                                    overflow: 'hidden',
                                    boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{
                                        padding: '1.25rem 1.5rem',
                                        borderBottom: '1px solid #f1f5f9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <i className="fas fa-link" style={{ color: '#cd6906', fontSize: '1rem' }}></i>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                                            {t('musteri.iliskiliKayitlar')}
                                        </h3>
                                    </div>
                                    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>

                                        {/* Kira Sözleşmeleri */}
                                        {relatedRecords.kiraSozlesmeleri.length > 0 && (
                                            <div style={{ flex: '1 1 340px' }}>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                                                    <i className="fas fa-key" style={{ marginRight: 6 }}></i>{t('musteri.kiraSozlesmeleri', { n: relatedRecords.kiraSozlesmeleri.length })}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                    {relatedRecords.kiraSozlesmeleri.map(ks => (
                                                        <div
                                                            key={ks.kiraSozlesmeID}
                                                            onClick={() => navigate(`/kiralik/sozlesme-detay/${ks.takipNo}`)}
                                                            style={{
                                                                padding: '0.875rem 1rem',
                                                                borderRadius: '0.75rem',
                                                                border: '1.5px solid #e2e8f0',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.75rem'
                                                            }}
                                                            onMouseOver={e => { e.currentTarget.style.borderColor = '#1a2340'; e.currentTarget.style.background = '#f8fafc'; }}
                                                            onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                                                        >
                                                            <div style={{
                                                                width: 36, height: 36, borderRadius: '0.5rem',
                                                                background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                            }}>
                                                                <i className="fas fa-file-contract" style={{ color: '#1a2340', fontSize: 14 }}></i>
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{ks.takipNo}</div>
                                                                {ks.gayrimenkulBaslik && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ks.gayrimenkulBaslik}</div>}
                                                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                                                    {new Date(ks.baslangicTarihi).toLocaleDateString('tr-TR')} — {new Date(ks.bitisTarihi).toLocaleDateString('tr-TR')}
                                                                </div>
                                                            </div>
                                                            <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2340' }}>₺{ks.kiraBedeli.toLocaleString('tr-TR')}</div>
                                                                <span style={{
                                                                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                                                                    background: ks.aktifMi ? '#dcfce7' : '#f1f5f9',
                                                                    color: ks.aktifMi ? '#15803d' : '#94a3b8'
                                                                }}>{ks.aktifMi ? t('durum.aktif') : t('durum.pasif')}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Emlak Sunumları */}
                                        {relatedRecords.emlakSunumlari.length > 0 && (
                                            <div style={{ flex: '1 1 340px' }}>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                                                    <i className="fas fa-building" style={{ marginRight: 6 }}></i>{t('musteri.emlakSunumlari', { n: relatedRecords.emlakSunumlari.length })}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                    {relatedRecords.emlakSunumlari.map(es => (
                                                        <div
                                                            key={es.sunumID}
                                                            onClick={() => navigate(`/emlak-sunumlari/${es.sunumID}`)}
                                                            style={{
                                                                padding: '0.875rem 1rem',
                                                                borderRadius: '0.75rem',
                                                                border: '1.5px solid #e2e8f0',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.75rem'
                                                            }}
                                                            onMouseOver={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.background = '#fffbf5'; }}
                                                            onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                                                        >
                                                            <div style={{
                                                                width: 36, height: 36, borderRadius: '0.5rem',
                                                                background: '#fff8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                            }}>
                                                                <i className="fas fa-presentation" style={{ color: '#cd6906', fontSize: 14 }}></i>
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{es.takipNo}</div>
                                                                {es.baslik && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{es.baslik}</div>}
                                                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{new Date(es.olusturmaTarihi).toLocaleDateString('tr-TR')}</div>
                                                            </div>
                                                            <div style={{ flexShrink: 0 }}>
                                                                <span style={{
                                                                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                                                                    background: es.durum === 'Aktif' ? '#dcfce7' : es.durum === 'Tamamlandı' ? '#dbeafe' : es.durum === 'İptal' ? '#fee2e2' : '#f1f5f9',
                                                                    color: es.durum === 'Aktif' ? '#15803d' : es.durum === 'Tamamlandı' ? '#1d4ed8' : es.durum === 'İptal' ? '#dc2626' : '#64748b'
                                                                }}>{es.durum}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* AKTİVİTELER TAB */}
                    {activeMainTab === 'aktiviteler' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            {/* Activity Feed - Notlar */}
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <h3 style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                    color: '#1e293b',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <StickyNote size={20} style={{ color: '#64748b' }} />
                                    {t('musteri.aktiviteAkisi')}
                                </h3>
                                <div style={{ 
                                    flex: 1, 
                                    overflowY: 'auto', 
                                    position: 'relative',
                                    paddingLeft: '2rem',
                                    maxHeight: '500px'
                                }}>
                                    {notlar.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#94a3b8', 
                                            padding: '2rem 0',
                                            fontSize: '0.875rem'
                                        }}>
                                            {t('musteri.notBulunamadi')}
                                        </div>
                                    ) : (
                                        <div style={{ position: 'relative' }}>
                                            {/* Timeline Line */}
                                            <div style={{
                                                position: 'absolute',
                                                left: '0.25rem',
                                                top: '0.5rem',
                                                bottom: '0.5rem',
                                                width: '1px',
                                                background: '#e2e8f0'
                                            }} />
                                            
                                            {notlar.map((not, index) => (
                                                <div key={not.notID} style={{
                                                    position: 'relative',
                                                    paddingBottom: index === notlar.length - 1 ? 0 : '1rem'
                                                }}>
                                                    {/* Timeline Dot - Hollow Ring */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: '-1.5rem',
                                                        top: '0.375rem',
                                                        width: '0.5rem',
                                                        height: '0.5rem',
                                                        borderRadius: '50%',
                                                        background: 'white',
                                                        border: '2px solid #4f46e5',
                                                        boxShadow: '0 0 0 2px white'
                                                    }} />
                                                    
                                                    {/* Note Content */}
                                                    <div style={{
                                                        marginLeft: '0.5rem'
                                                    }}>
                                                        <div style={{
                                                            fontSize: '0.625rem',
                                                            letterSpacing: '0.02em',
                                                            color: '#94a3b8',
                                                            fontWeight: '500',
                                                            marginBottom: '0.25rem'
                                                        }}>
                                                            {not.ekleyen?.adSoyad} • {new Date(not.eklenmeTarihi).toLocaleString('tr-TR')}
                                                        </div>
                                                        <div style={{
                                                            background: '#f8fafc',
                                                            padding: '0.5rem 0.75rem',
                                                            borderRadius: '0.375rem',
                                                            borderLeft: '3px solid #e2e8f0',
                                                            fontSize: '0.875rem',
                                                            color: '#475569',
                                                            lineHeight: '1.5'
                                                        }}>
                                                            {not.aciklama}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    )}

                    {/* DOSYALAR TAB */}
                    {activeMainTab === 'smsler' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <SmsGecmisi cariID={musteri?.cariID} refreshKey={smsRefreshKey} />
                        </Col>
                    </Row>
                    )}

                    {/* ---- Sunumlar Sekmesi ---- */}
                    {activeMainTab === 'sunumlar' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            <div style={{ background: 'white', border: '1px solid #f1f5f9', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>
                                        <i className="fas fa-desktop" style={{ marginRight: 8, color: '#2563eb' }} />
                                        Müşteri Sunumları
                                    </h3>
                                    <button
                                        onClick={handleSunumOlustur}
                                        disabled={sunumOlusturuluyor}
                                        style={{
                                            padding: '0.45rem 0.9rem', background: '#2563eb', border: 'none', borderRadius: '0.5rem',
                                            color: '#fff', fontSize: '0.8rem', fontWeight: 600,
                                            cursor: sunumOlusturuluyor ? 'not-allowed' : 'pointer', opacity: sunumOlusturuluyor ? 0.6 : 1,
                                        }}>
                                        <i className="fas fa-plus" style={{ marginRight: 6 }} />
                                        {sunumOlusturuluyor ? 'Oluşturuluyor...' : 'Sunum Oluştur'}
                                    </button>
                                </div>

                                {sunumlarLoading ? (
                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                                        <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />Yükleniyor...
                                    </div>
                                ) : musteriSunumlari.length === 0 ? (
                                    <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                                        <i className="fas fa-desktop" style={{ fontSize: 26, display: 'block', marginBottom: 10, opacity: 0.5 }} />
                                        Bu müşteri için henüz sunum oluşturulmamış.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {musteriSunumlari.map((s: any) => {
                                            const devamEdiyor = s.durum === 'Taslak' || s.durum === 'Devam Ediyor';
                                            const durumRenk = s.durum === 'Olumlu Sonuçlandı' ? { bg: '#f0fdf4', color: '#15803d' }
                                                : s.durum === 'Olumsuz Sonuçlandı' || s.durum === 'İptal Edildi' ? { bg: '#fff1f2', color: '#be123c' }
                                                : s.durum === 'Tamamlandı' ? { bg: '#eff6ff', color: '#1d4ed8' }
                                                : { bg: '#fef3c7', color: '#92400e' };
                                            return (
                                                <div key={s.sunumID}
                                                    onClick={() => navigate(`/musteri-sunumlari/${s.sunumID}`)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 14, padding: '0.75rem 1rem',
                                                        border: '1px solid #e5e7eb', borderRadius: '0.625rem', cursor: 'pointer',
                                                        transition: 'all 0.15s', background: '#fff',
                                                    }}
                                                    onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#2563eb'; }}
                                                    onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                                                                {s.baslik || 'Sunum'}
                                                            </span>
                                                            <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 9px', borderRadius: '9999px', background: durumRenk.bg, color: durumRenk.color }}>
                                                                {s.durum}
                                                            </span>
                                                            {devamEdiyor && (
                                                                <span style={{ fontSize: '0.68rem', color: '#92400e' }}>
                                                                    <i className="fas fa-circle-exclamation" style={{ marginRight: 4 }} />devam ediyor
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 3 }}>
                                                            {s.takipNo} · {new Date(s.olusturmaTarihi).toLocaleDateString('tr-TR')}
                                                            {typeof s.daireAdet === 'number' ? ` · ${s.daireAdet} öğe` : ''}
                                                            {s.olusturanAdSoyad ? ` · ${s.olusturanAdSoyad}` : ''}
                                                        </div>
                                                    </div>
                                                    <i className="fas fa-chevron-right" style={{ color: '#d1d5db', fontSize: '0.8rem' }} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    )}

                    {activeMainTab === 'dosyalar' && (
                    <Row className="mb-3">
                        <Col md={12}>
                            {/* File Cards - Dosyalar */}
                            <div style={{
                                background: 'white',
                                border: '1px solid #f1f5f9',
                                borderRadius: '1rem',
                                padding: '1.5rem',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <h3 style={{
                                    fontSize: '1.125rem',
                                    fontWeight: '600',
                                    color: '#1e293b',
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <FileText size={20} style={{ color: '#64748b' }} />
                                    {t('musteri.ekliDosyalar')}
                                </h3>
                                <div style={{ 
                                    flex: 1, 
                                    overflowY: 'auto',
                                    maxHeight: '500px'
                                }}>
                                    {dosyalar.length === 0 ? (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#94a3b8', 
                                            padding: '2rem 0',
                                            fontSize: '0.875rem'
                                        }}>
                                            {t('musteri.dosyaBulunamadi')}
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}>
                                            {dosyalar.map((dosya) => {
                                                const ext = dosya.dosyaAdi.toLowerCase().split('.').pop() || '';
                                                const isPdf = ext === 'pdf';
                                                const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
                                                
                                                return (
                                                    <div 
                                                        key={dosya.dosyaID} 
                                                        className="file-list-item"
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            padding: '0.75rem',
                                                            borderRadius: '0.5rem',
                                                            transition: 'background 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = '#f8fafc';
                                                            const actions = e.currentTarget.querySelector('.file-actions') as HTMLElement;
                                                            if (actions) actions.style.opacity = '1';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = 'transparent';
                                                            const actions = e.currentTarget.querySelector('.file-actions') as HTMLElement;
                                                            if (actions) actions.style.opacity = '0';
                                                        }}
                                                    >
                                                        {/* Sol taraf: İkon + Dosya Bilgisi */}
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.75rem',
                                                            flex: 1,
                                                            minWidth: 0
                                                        }}>
                                                            {/* Küçük İkon */}
                                                            <div style={{ flexShrink: 0 }}>
                                                                {isPdf ? (
                                                                    <FileText size={20} style={{ color: '#dc2626' }} />
                                                                ) : isImage ? (
                                                                    <FileImage size={20} style={{ color: '#10b981' }} />
                                                                ) : (
                                                                    <File size={20} style={{ color: '#64748b' }} />
                                                                )}
                                                            </div>
                                                            
                                                            {/* Dosya Adı ve Tarih */}
                                                            <div style={{ 
                                                                flex: 1,
                                                                minWidth: 0,
                                                                overflow: 'hidden'
                                                            }}>
                                                                <div style={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#0f172a',
                                                                    fontWeight: '500',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}>
                                                                    {dosya.aciklama}
                                                                </div>
                                                                <div style={{
                                                                    fontSize: '0.625rem',
                                                                    color: '#94a3b8',
                                                                    marginTop: '0.125rem'
                                                                }}>
                                                                    {new Date(dosya.eklenmeTarihi).toLocaleDateString('tr-TR')} · {ext.toUpperCase()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Sağ taraf: Hover'da görünen butonlar */}
                                                        <div 
                                                            className="file-actions"
                                                            style={{
                                                                display: 'flex',
                                                                gap: '0.5rem',
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            {(isPdf || isImage) && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePreview(dosya.dosyaID, dosya.dosyaAdi);
                                                                    }}
                                                                    style={{
                                                                        padding: '0.375rem',
                                                                        background: 'transparent',
                                                                        border: 'none',
                                                                        borderRadius: '0.375rem',
                                                                        color: '#10b981',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        transition: 'background 0.2s'
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f0fdf4'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                    title="Önizle"
                                                                >
                                                                    <Eye size={16} />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDosyaDownload(dosya.dosyaID);
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    borderRadius: '0.375rem',
                                                                    color: '#4f46e5',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                onMouseOver={(e) => e.currentTarget.style.background = '#eef2ff'}
                                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                title={t('btn.indir')}
                                                            >
                                                                <Download size={16} />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    // Sil fonksiyonu buraya
                                                                }}
                                                                style={{
                                                                    padding: '0.375rem',
                                                                    background: 'transparent',
                                                                    border: 'none',
                                                                    borderRadius: '0.375rem',
                                                                    color: '#ef4444',
                                                                    cursor: 'pointer',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                onMouseOver={(e) => e.currentTarget.style.background = '#fef2f2'}
                                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                title={t('btn.sil')}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    )}
                </div>
            </section>

            {/* Müşteri Düzenleme Drawer */}
            {showModal && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); if (id === 'yeni') navigate('/musteriler'); } }}
                >
                    {/* Backdrop */}
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
                    {/* Drawer */}
                    <div style={{ position: 'relative', width: '100%', maxWidth: 1020, height: '92vh', maxHeight: '92vh', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', borderRadius: 16, overflowY: 'hidden' }}>
                        {/* Sticky Header */}
                        <div style={{ flexShrink: 0, padding: '20px 24px 0', borderBottom: '1px solid #f3f4f6', background: '#fff', zIndex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                                <div>
                                    <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{id === 'yeni' ? t('musteri.yeniMusteri') : t('musteri.musteriDuzenle')}</h2>
                                </div>
                                <button
                                    onClick={() => { setShowModal(false); if (id === 'yeni') navigate('/musteriler'); }}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#9ca3af', borderRadius: 6, display: 'flex', alignItems: 'center' }}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: 24 }}>
                                {(['temel', 'firma'] as const).map((tab) => {
                                    const labels = { temel: t('musteri.temelBilgiler'), firma: t('musteri.firmaBilgileri') };
                                    const active = activeFormTab === tab;
                                    return (
                                        <button
                                            key={tab}
                                            type="button"
                                            onClick={() => setActiveFormTab(tab)}
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 12px',
                                                fontSize: 13, fontWeight: 500,
                                                color: active ? '#111827' : '#6b7280',
                                                borderBottom: active ? '2px solid #111827' : '2px solid transparent',
                                                transition: 'color 0.15s, border-color 0.15s',
                                            }}
                                        >{labels[tab]}</button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Scrollable Body */}
                        <Form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 24, flex: 1 }}>

                                {/* Tab: Temel Bilgiler */}
                                {activeFormTab === 'temel' && (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20 }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    {t('label.adSoyad')} <span style={{ color: '#f43f5e' }}>*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.adiSoyadi}
                                                    onChange={(e) => setFormData({ ...formData, adiSoyadi: e.target.value })}
                                                    required maxLength={100}
                                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                    onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.tcKimlikNo')}</label>
                                                <input
                                                    type="text"
                                                    value={formData.tc || ''}
                                                    onChange={(e) => setFormData({ ...formData, tc: e.target.value || null })}
                                                    maxLength={11}
                                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                    onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                />
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('label.tur')}</label>
                                                    <select
                                                        value={formData.turu}
                                                        onChange={(e) => setFormData({ ...formData, turu: e.target.value })}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    >
                                                        <option value="Müşteri">{t('musteri.musteri')}</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.altKategori')}</label>
                                                    <select
                                                        value={formData.turu2 || ''}
                                                        onChange={(e) => setFormData({ ...formData, turu2: e.target.value || null })}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    >
                                                        <option value="">{t('label.seciniz')}</option>
                                                        {cariAltKategoriler.map(k => (
                                                            <option key={k.cariAltKategoriID} value={k.altKategoriAdi}>{k.altKategoriAdi}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.kaynak')}</label>
                                                <select
                                                    value={formData.kaynak || ''}
                                                    onChange={(e) => setFormData({ ...formData, kaynak: e.target.value || null })}
                                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                >
                                                    <option value="">{t('label.seciniz')}</option>
                                                    {leadKaynaklar.filter(k => k.aktifMi).map(k => (
                                                        <option key={k.id} value={k.ad}>{k.ad}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                            <div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    {t('musteri.telefon1')} <span style={{ color: '#f43f5e' }}>*</span>
                                                    {iysAktarimAktif && (iysIzinlerMd.some(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                                        ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                                        : formData.telefon1 && <button type="button" onClick={() => setIysModalMd({ iletisimTipi: 'TELEFON1', iletisimVerisi: formData.telefon1 })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>{t('musteri.iysIzniAl')}</button>)}
                                                </label>
                                                <IysKilitliAlan
                                                    aktifIzin={iysIzinlerMd.find(i => i.iletisimTipi === 'TELEFON1' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                                    cariID={musteri?.cariID ?? 0}
                                                    takipNo={musteri?.takipNo ?? ''}
                                                    ekleyenID={user?.kullaniciID ?? 0}
                                                    iletisimTipiLabel="Telefon 1"
                                                    onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri?.cariID}`); setIysIzinlerMd(r.data); } catch {} }}
                                                    disabled={!iysAktarimAktif}
                                                >{(readOnly) => (
                                                    <input
                                                        type="text"
                                                        value={formatPhoneNumber(formData.telefon1)}
                                                        onChange={(e) => setFormData({ ...formData, telefon1: unformatPhoneNumber(e.target.value) })}
                                                        required maxLength={14} placeholder="(5XX)XXX-XX-XX"
                                                        readOnly={readOnly}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: readOnly ? '#f9fafb' : '#fff', color: '#111827', boxSizing: 'border-box', cursor: readOnly ? 'not-allowed' : undefined }}
                                                        onFocus={e => { if (!readOnly) { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; } }}
                                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                    />
                                                )}</IysKilitliAlan>
                                            </div>
                                            <div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    {t('musteri.telefon2')}
                                                    {iysAktarimAktif && (iysIzinlerMd.some(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                                        ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                                        : formData.telefon2 && <button type="button" onClick={() => setIysModalMd({ iletisimTipi: 'TELEFON2', iletisimVerisi: formData.telefon2! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>{t('musteri.iysIzniAl')}</button>)}
                                                </label>
                                                <IysKilitliAlan
                                                    aktifIzin={iysIzinlerMd.find(i => i.iletisimTipi === 'TELEFON2' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                                    cariID={musteri?.cariID ?? 0}
                                                    takipNo={musteri?.takipNo ?? ''}
                                                    ekleyenID={user?.kullaniciID ?? 0}
                                                    iletisimTipiLabel="Telefon 2"
                                                    onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri?.cariID}`); setIysIzinlerMd(r.data); } catch {} }}
                                                    disabled={!iysAktarimAktif}
                                                >{(readOnly) => (
                                                    <input
                                                        type="text"
                                                        value={formData.telefon2 ? formatPhoneNumber(formData.telefon2) : ''}
                                                        onChange={(e) => setFormData({ ...formData, telefon2: e.target.value ? unformatPhoneNumber(e.target.value) : null })}
                                                        maxLength={14} placeholder="(5XX)XXX-XX-XX"
                                                        readOnly={readOnly}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: readOnly ? '#f9fafb' : '#fff', color: '#111827', boxSizing: 'border-box', cursor: readOnly ? 'not-allowed' : undefined }}
                                                        onFocus={e => { if (!readOnly) { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; } }}
                                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                    />
                                                )}</IysKilitliAlan>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                            <div>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                                    {t('label.ePosta')}
                                                    {iysAktarimAktif && (iysIzinlerMd.some(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi)
                                                        ? <i className="fas fa-check-circle" title="IYS İzni Alındı" style={{ color: '#22c55e' }}></i>
                                                        : formData.ePosta && <button type="button" onClick={() => setIysModalMd({ iletisimTipi: 'EPOSTA', iletisimVerisi: formData.ePosta! })} style={{ background: 'none', border: '1px solid #e67d21', color: '#e67d21', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 11, lineHeight: '18px' }}>{t('musteri.iysIzniAl')}</button>)}
                                                </label>
                                                <IysKilitliAlan
                                                    aktifIzin={iysIzinlerMd.find(i => i.iletisimTipi === 'EPOSTA' && i.izinDurumu === 'ONAY' && i.aktifMi) ?? null}
                                                    cariID={musteri?.cariID ?? 0}
                                                    takipNo={musteri?.takipNo ?? ''}
                                                    ekleyenID={user?.kullaniciID ?? 0}
                                                    iletisimTipiLabel="E-Posta"
                                                    onUnlocked={async () => { try { const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri?.cariID}`); setIysIzinlerMd(r.data); } catch {} }}
                                                    disabled={!iysAktarimAktif}
                                                >{(readOnly) => (
                                                    <input
                                                        type="email"
                                                        value={formData.ePosta || ''}
                                                        onChange={(e) => setFormData({ ...formData, ePosta: e.target.value || null })}
                                                        maxLength={100}
                                                        readOnly={readOnly}
                                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: readOnly ? '#f9fafb' : '#fff', color: '#111827', boxSizing: 'border-box', cursor: readOnly ? 'not-allowed' : undefined }}
                                                        onFocus={e => { if (!readOnly) { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; } }}
                                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                    />
                                                )}</IysKilitliAlan>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('label.adres')}</label>
                                                <input
                                                    type="text"
                                                    value={formData.adres || ''}
                                                    onChange={(e) => setFormData({ ...formData, adres: e.target.value || null })}
                                                    maxLength={500}
                                                    style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                    onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                    onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Tab: Firma Bilgileri */}
                                {activeFormTab === 'firma' && (
                                    <>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.firmaAdi')}</label>
                                            <input
                                                type="text"
                                                value={formData.firmaAdi || ''}
                                                onChange={(e) => setFormData({ ...formData, firmaAdi: e.target.value || null })}
                                                maxLength={100}
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.vergiDairesi')}</label>
                                            <input
                                                type="text"
                                                value={formData.cariVD || ''}
                                                onChange={(e) => setFormData({ ...formData, cariVD: e.target.value || null })}
                                                maxLength={100}
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.vergiNo')}</label>
                                            <input
                                                type="text"
                                                value={formData.cariVN || ''}
                                                onChange={(e) => setFormData({ ...formData, cariVN: e.target.value || null })}
                                                maxLength={50}
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' }}
                                                onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                                onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* IYS Kayıtları */}
                                <div style={{ padding: '0 28px 4px' }}>
                                    <IysKayitListesi izinler={iysIzinlerMd} />
                                </div>

                            </div>

                            {/* Sticky Footer */}
                            <div style={{ flexShrink: 0, padding: '20px 28px', borderTop: '1px solid #e5e7eb', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div
                                        onClick={() => setFormData({ ...formData, aktifMi: !formData.aktifMi })}
                                        style={{ width: 44, height: 24, borderRadius: 12, background: formData.aktifMi ? '#cd6906' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                                    >
                                        <div style={{ position: 'absolute', top: 3, left: formData.aktifMi ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                                    </div>
                                    <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{t('durum.aktif')}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); if (id === 'yeni') navigate('/musteriler'); }}
                                    style={{ padding: '10px 20px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'background 0.15s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                >{t('btn.iptal')}</button>
                                <button
                                    type="submit"
                                    style={{ padding: '10px 24px', background: '#cd6906', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'background 0.15s, box-shadow 0.15s' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                ><Check size={16} />{t('btn.kaydet')}</button>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            )}

            {/* Not Ekleme Modal */}
            <Modal show={showNotModal} onHide={() => setShowNotModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('musteri.notEkle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleNotSubmit}>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>{t('musteri.aciklama')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={notFormData.aciklama}
                                onChange={(e) => setNotFormData({ aciklama: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowNotModal(false)}>
                            {t('btn.iptal')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('btn.kaydet')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Dosya Ekleme Modal */}
            <Modal show={showDosyaModal} onHide={() => setShowDosyaModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('musteri.dosyaEkle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!dosyaFormData.dosya || !musteri?.takipNo) {
                        toast.error(t('msg.zorunluAlanlar'));
                        return;
                    }

                    const formData = new FormData();
                    formData.append('file', dosyaFormData.dosya);
                    formData.append('takipNo', musteri.takipNo);
                    formData.append('aciklama', dosyaFormData.aciklama);

                    try {
                        await axiosInstance.post('/api/Dosyalar/upload', formData);
                        toast.success(t('msg.islemBasarili'));
                        setShowDosyaModal(false);
                        setDosyaFormData({ dosya: null, aciklama: '' });
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                        fetchDosyalar(); // Dosya listesini güncelle
                    } catch (error) {
                        console.error('Dosya yükleme hatası:', error);
                        toast.error(t('msg.islemHatasi'));
                    }
                }}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('musteri.dosyaSec')}</Form.Label>
                            <Form.Control
                                ref={fileInputRef}
                                type="file"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const file = e.target.files?.[0] || null;
                                    setDosyaFormData(prev => ({ ...prev, dosya: file }));
                                }}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('musteri.aciklama')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={dosyaFormData.aciklama}
                                onChange={(e) => setDosyaFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDosyaModal(false)}>
                            {t('btn.iptal')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('musteri.yukle')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Dosya Önizleme Modal */}
            <Modal 
                show={showPreviewModal} 
                onHide={() => {
                    setShowPreviewModal(false);
                    if (previewUrl) {
                        window.URL.revokeObjectURL(previewUrl);
                    }
                    setPreviewUrl('');
                }}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('musteri.dosyaOnizleme')}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ 
                    padding: 0, 
                    minHeight: '70vh', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: '#f8fafc'
                }}>
                    {previewType.startsWith('image/') ? (
                        <div style={{
                            width: '100%',
                            height: '70vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '1rem'
                        }}>
                            <img 
                                src={previewUrl} 
                                alt="Önizleme" 
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    ) : previewType === 'application/pdf' ? (
                        <iframe
                            src={previewUrl}
                            style={{
                                width: '100%',
                                height: '70vh',
                                border: 'none'
                            }}
                            title="PDF Önizleme"
                        />
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            color: '#94a3b8',
                            padding: '2rem'
                        }}>
                            {t('musteri.dosyaOnizlemeyeUygun')}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowPreviewModal(false);
                        if (previewUrl) {
                            window.URL.revokeObjectURL(previewUrl);
                        }
                        setPreviewUrl('');
                    }}>
                        {t('btn.kapat')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ödeme Düzenle Modal */}
            {showOdemeEditModal && selectedOdeme && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowOdemeEditModal(false)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                    <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Edit size={17} style={{ color: '#cd6906' }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{t('musteri.odemeDuzenle')}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>#{selectedOdeme.islemTakipNo}</div>
                                </div>
                            </div>
                            <button onClick={() => setShowOdemeEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9ca3af'; }}>
                                <X size={18} />
                            </button>
                        </div>
                        {/* Body */}
                        <form onSubmit={handleOdemeGuncelle}>
                            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.vadeTarihi')}</label>
                                        <TrDateInput value={odemeEditForm.vadeTarihi} onChange={e => setOdemeEditForm(p => ({ ...p, vadeTarihi: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.tahsilatTarihi')}</label>
                                        <TrDateInput value={odemeEditForm.tahsilatTarihi} onChange={e => setOdemeEditForm(p => ({ ...p, tahsilatTarihi: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.tutar')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <input type="number" step="0.01" min="0" required value={odemeEditForm.miktar} onChange={e => setOdemeEditForm(p => ({ ...p, miktar: parseFloat(e.target.value) || 0 }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.odemeTuru')}</label>
                                        <select value={odemeEditForm.odemeTuru} onChange={e => setOdemeEditForm(p => ({ ...p, odemeTuru: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Taksit">{t('musteri.taksit')}</option>
                                            <option value="Peşinat">{t('musteri.pesinat')}</option>
                                            <option value="Senet">{t('musteri.senet')}</option>
                                            <option value="Nakit">{t('musteri.nakit')}</option>
                                            <option value="KrediKarti">{t('musteri.krediKarti')}</option>
                                            <option value="Havale">{t('musteri.havale')}</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('label.durum')}</label>
                                        <select value={odemeEditForm.odemeDurumu} onChange={e => setOdemeEditForm(p => ({ ...p, odemeDurumu: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Bekliyor">{t('lead.bekliyor')}</option>
                                            <option value="Odendi">{t('musteri.odendi')}</option>
                                        </select>
                                    </div>
                                    {odemeEditForm.odemeDurumu === 'Odendi' && (
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('kasa.kasa')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                            <select required value={odemeEditForm.kasaID ?? ''} onChange={e => setOdemeEditForm(p => ({ ...p, kasaID: e.target.value ? Number(e.target.value) : null }))}
                                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                                <option value="">— Seçiniz —</option>
                                                {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.aciklama')}</label>
                                    <textarea rows={3} value={odemeEditForm.aciklama} onChange={e => setOdemeEditForm(p => ({ ...p, aciklama: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                </div>
                            </div>
                            {/* Footer */}
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" onClick={() => setShowOdemeEditModal(false)}
                                    style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                                    {t('btn.iptal')}
                                </button>
                                <button type="submit"
                                    style={{ padding: '9px 22px', background: '#cd6906', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; }}>
                                    <Check size={15} /> {t('btn.kaydet')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Toplu Ödeme Planla Modal */}
            <Modal show={showTopluPlanlaModal} onHide={() => setShowTopluPlanlaModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>{t('musteri.topluOdemePlanla')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{t('musteri.toplamTutar')} *</Form.Label>
                            <Form.Control type="text" placeholder="0" value={topluPlanlaFormData.toplamTutar}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, toplamTutar: formatMoneyInput(e.target.value) }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{t('musteri.taksitSayisi')} *</Form.Label>
                            <Form.Control type="number" min={1} value={topluPlanlaFormData.taksitSayisi}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, taksitSayisi: e.target.value }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{t('musteri.baslamaTarihi')} *</Form.Label>
                            <Form.Control type="text" placeholder="GG.AA.YYYY" maxLength={10} value={topluPlanlaFormData.baslamaTarihi}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, baslamaTarihi: maskTRDate(e.target.value) }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{t('musteri.aralikAy')}</Form.Label>
                            <Form.Control type="number" min={1} value={topluPlanlaFormData.aralikAy}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, aralikAy: e.target.value }))} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{t('musteri.odemeTuru')}</Form.Label>
                            <Form.Select value={topluPlanlaFormData.odemeTuru} onChange={e => setTopluPlanlaFormData(f => ({ ...f, odemeTuru: e.target.value }))}>
                                <option value="Nakit">{t('musteri.nakit')}</option>
                                <option value="Senet">{t('musteri.senet')}</option>
                                <option value="Cek">{t('musteri.cek')}</option>
                                <option value="Takas">{t('musteri.takas')}</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>{t('musteri.aciklama')}</Form.Label>
                            <Form.Control type="text" value={topluPlanlaFormData.aciklama}
                                onChange={e => setTopluPlanlaFormData(f => ({ ...f, aciklama: e.target.value }))} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTopluPlanlaModal(false)}>{t('btn.iptal')}</Button>
                    <Button variant="primary" style={{ background: '#cd6906', borderColor: '#cd6906' }} onClick={handleSaveTopluPlanla} disabled={saving}>
                        {saving ? t('musteri.kaydediliyor') : t('musteri.planla')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Tahsilat Yap Modal */}
            {showOdemeYapModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowOdemeYapModal(false); setIsPlanTaksitOdeme(false); setPlanTaksitForOdeme(null); }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                    <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fas fa-plus" style={{ color: '#cd6906', fontSize: 14 }}></i>
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{t('musteri.tahsilatEkle')}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                        {odemeYapTaksit && <><strong>{odemeYapTaksit.aciklama}</strong> — ₺{formatCurrency(odemeYapTaksit.miktar)}</>}
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => { setShowOdemeYapModal(false); setIsPlanTaksitOdeme(false); setPlanTaksitForOdeme(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9ca3af'; }}>
                                <X size={18} />
                            </button>
                        </div>
                        {/* Body */}
                        <form onSubmit={e => { e.preventDefault(); handleSaveOdemeYap(); }}>
                            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '65vh', overflowY: 'auto' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.tahsilatTarihi')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <TrDateInput required value={odemeYapFormData.tarih}
                                            onChange={e => setOdemeYapFormData(f => ({ ...f, tarih: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.odemeTuru')}</label>
                                        <select value={odemeYapFormData.odemeTuru}
                                            onChange={e => setOdemeYapFormData(f => ({ ...f, odemeTuru: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Nakit">{t('musteri.nakit')}</option>
                                            <option value="Senet">{t('musteri.senet')}</option>
                                            <option value="Cek">{t('musteri.cek')}</option>
                                            <option value="Takas">{t('musteri.takas')}</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('kasa.kasa')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                    <select required value={odemeYapFormData.kasaID}
                                        onChange={e => setOdemeYapFormData(f => ({ ...f, kasaID: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                        <option value="">— Kasa seçin —</option>
                                        {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>)}
                                    </select>
                                </div>
                                {(sozlesmeler.length > 0 || relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0) && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                            İlişkili Sözleşme <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(opsiyonel)</span>
                                        </label>
                                        <select
                                            value={odemeYapFormData.sozlesmeSecimi ? `s:${odemeYapFormData.sozlesmeSecimi}` : odemeYapFormData.kiraSozlesmeSecimi ? `k:${odemeYapFormData.kiraSozlesmeSecimi}` : ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                if (!val) setOdemeYapFormData(f => ({ ...f, sozlesmeSecimi: '', kiraSozlesmeSecimi: '' }));
                                                else if (val.startsWith('s:')) setOdemeYapFormData(f => ({ ...f, sozlesmeSecimi: val.slice(2), kiraSozlesmeSecimi: '' }));
                                                else if (val.startsWith('k:')) setOdemeYapFormData(f => ({ ...f, kiraSozlesmeSecimi: val.slice(2), sozlesmeSecimi: '' }));
                                            }}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="">— Sözleşme seçilmedi —</option>
                                            {sozlesmeler.length > 0 && (
                                                <optgroup label="Satış Sözleşmeleri">
                                                    {sozlesmeler.map(s => (
                                                        <option key={s.sozlesmeID} value={`s:${s.sozlesmeTakipNo}`}>
                                                            {s.sozlesmeTakipNo} — {s.daire?.blok?.proje?.projeAdi || ''} {s.daire?.daireNo ? `/ Daire ${s.daire.daireNo}` : ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                            {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0 && (
                                                <optgroup label="Kira Sözleşmeleri">
                                                    {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).map(ks => (
                                                        <option key={ks.kiraSozlesmeID} value={`k:${ks.takipNo}`}>
                                                            {ks.takipNo} — {ks.gayrimenkulBaslik || ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </select>
                                    </div>
                                )}
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.aciklama')}</label>
                                    <textarea rows={3} value={odemeYapFormData.aciklama}
                                        onChange={e => setOdemeYapFormData(f => ({ ...f, aciklama: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                </div>
                            </div>
                            {/* Footer */}
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" onClick={() => { setShowOdemeYapModal(false); setIsPlanTaksitOdeme(false); setPlanTaksitForOdeme(null); }}
                                    style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                                    {t('btn.iptal')}
                                </button>
                                <button type="submit" disabled={saving}
                                    style={{ padding: '9px 22px', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', background: '#cd6906', display: 'flex', alignItems: 'center', gap: 7 }}
                                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                                    <Check size={15} /> {saving ? t('musteri.kaydediliyor') : t('musteri.tahsilatKaydet')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Tahsilat / Ödeme Ekle Modal */}
            {showHareketModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowHareketModal(false)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)' }} />
                    <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', zIndex: 1 }} onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: 10,
                                    background: hareketFormData.odemeYonu === 'Tahsilat' ? '#fff7ed' : '#fef2f2',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <i className={`fas ${hareketFormData.odemeYonu === 'Tahsilat' ? 'fa-plus' : 'fa-minus'}`}
                                        style={{ color: hareketFormData.odemeYonu === 'Tahsilat' ? '#cd6906' : '#dc2626', fontSize: 14 }}></i>
                                </div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                                        {hareketFormData.odemeYonu === 'Tahsilat' ? t('musteri.tahsilatEkle') : t('musteri.odemeEkle')}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>{musteri?.adiSoyadi}</div>
                                </div>
                            </div>
                            <button onClick={() => setShowHareketModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#9ca3af', display: 'flex', alignItems: 'center' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9ca3af'; }}>
                                <X size={18} />
                            </button>
                        </div>
                        {/* Body */}
                        <form onSubmit={e => { e.preventDefault(); handleSaveHareket(); }}>
                            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '65vh', overflowY: 'auto' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.tutar')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <input type="number" step="0.01" min="0" required
                                            value={hareketFormData.miktar}
                                            onChange={e => setHareketFormData(p => ({ ...p, miktar: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.vadeTarihi')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <TrDateInput required
                                            value={hareketFormData.vadeTarihi}
                                            onChange={e => setHareketFormData(p => ({ ...p, vadeTarihi: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                                            onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                            onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.odemeTuru')}</label>
                                        <select value={hareketFormData.odemeTuru}
                                            onChange={e => setHareketFormData(p => ({ ...p, odemeTuru: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Nakit">{t('musteri.nakit')}</option>
                                            <option value="Senet">{t('musteri.senet')}</option>
                                            <option value="Cek">{t('musteri.cek')}</option>
                                            <option value="Takas">{t('musteri.takas')}</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('label.durum')}</label>
                                        <select value={hareketFormData.odemeDurumu}
                                            onChange={e => setHareketFormData(p => ({ ...p, odemeDurumu: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="Bekliyor">{t('lead.bekliyor')}</option>
                                            <option value="Odendi">{t('musteri.odendi')}</option>
                                        </select>
                                    </div>
                                </div>
                                {hareketFormData.odemeDurumu === 'Odendi' && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('kasa.kasa')} <span style={{ color: '#f43f5e' }}>*</span></label>
                                        <select value={hareketFormData.kasaID}
                                            onChange={e => setHareketFormData(p => ({ ...p, kasaID: e.target.value }))}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="">— Kasa seçin —</option>
                                            {kasalar.map(k => <option key={k.kasaID} value={k.kasaID}>{k.kasaAdi} ({k.kasaTuru === 'Nakit' ? '💵' : '🏦'})</option>)}
                                        </select>
                                    </div>
                                )}
                                {/* Satış Sözleşmesi Seçimi */}
                                {(sozlesmeler.length > 0 || relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0) && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                                            İlişkili Sözleşme <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(opsiyonel)</span>
                                        </label>
                                        <select
                                            value={hareketFormData.sozlesmeSecimi || hareketFormData.kiraSozlesmeSecimi
                                                ? (hareketFormData.sozlesmeSecimi ? `s:${hareketFormData.sozlesmeSecimi}` : `k:${hareketFormData.kiraSozlesmeSecimi}`)
                                                : ''}
                                            onChange={e => {
                                                const val = e.target.value;
                                                if (!val) {
                                                    setHareketFormData(p => ({ ...p, sozlesmeSecimi: '', kiraSozlesmeSecimi: '' }));
                                                } else if (val.startsWith('s:')) {
                                                    setHareketFormData(p => ({ ...p, sozlesmeSecimi: val.slice(2), kiraSozlesmeSecimi: '' }));
                                                } else if (val.startsWith('k:')) {
                                                    setHareketFormData(p => ({ ...p, kiraSozlesmeSecimi: val.slice(2), sozlesmeSecimi: '' }));
                                                }
                                            }}
                                            style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }}>
                                            <option value="">— Sözleşme seçilmedi —</option>
                                            {sozlesmeler.length > 0 && (
                                                <optgroup label="Satış Sözleşmeleri">
                                                    {sozlesmeler.map(s => (
                                                        <option key={s.sozlesmeID} value={`s:${s.sozlesmeTakipNo}`}>
                                                            {s.sozlesmeTakipNo} — {s.daire?.blok?.proje?.projeAdi || ''} {s.daire?.daireNo ? `/ Daire ${s.daire.daireNo}` : ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                            {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).length > 0 && (
                                                <optgroup label="Kira Sözleşmeleri">
                                                    {relatedRecords.kiraSozlesmeleri.filter(ks => ks.aktifMi).map(ks => (
                                                        <option key={ks.kiraSozlesmeID} value={`k:${ks.takipNo}`}>
                                                            {ks.takipNo} — {ks.gayrimenkulBaslik || ''}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </select>
                                        {(hareketFormData.sozlesmeSecimi || hareketFormData.kiraSozlesmeSecimi) && (
                                            <div style={{ marginTop: 6, fontSize: 12, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <i className="fas fa-link" style={{ fontSize: 10 }}></i> Bu hareket seçili sözleşme ile ilişkilendirilecek
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>{t('musteri.aciklama')}</label>
                                    <textarea rows={3} value={hareketFormData.aciklama}
                                        onChange={e => setHareketFormData(p => ({ ...p, aciklama: e.target.value }))}
                                        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' as const }}
                                        onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.boxShadow = '0 0 0 3px rgba(205,105,6,0.1)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }} />
                                </div>
                            </div>
                            {/* Footer */}
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button type="button" onClick={() => setShowHareketModal(false)}
                                    style={{ padding: '9px 18px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                                    {t('btn.iptal')}
                                </button>
                                <button type="submit" disabled={hareketSaving}
                                    style={{
                                        padding: '9px 22px', border: 'none', color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                        background: hareketFormData.odemeYonu === 'Tahsilat' ? '#cd6906' : '#dc2626',
                                        display: 'flex', alignItems: 'center', gap: 7,
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
                                    <Check size={15} /> {hareketSaving ? t('musteri.kaydediliyor') : t('btn.kaydet')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showSmsModal && musteri && (
                <SmsGonderModal
                    onClose={() => setShowSmsModal(false)}
                    onSent={() => setSmsRefreshKey(k => k + 1)}
                    telefonlar={[
                        ...(musteri.telefon1 ? [{ numara: musteri.telefon1, etiket: 'Telefon 1' }] : []),
                        ...(musteri.telefon2 ? [{ numara: musteri.telefon2, etiket: 'Telefon 2' }] : []),
                    ]}
                    cariID={musteri.cariID}
                    musteriAdi={musteri.adiSoyadi}
                />
            )}

            {iysModalMd && musteri?.cariID && (
                <IysIzinModal
                    cariID={musteri.cariID}
                    iletisimTipi={iysModalMd.iletisimTipi}
                    iletisimVerisi={iysModalMd.iletisimVerisi}
                    onClose={() => setIysModalMd(null)}
                    onSuccess={async () => {
                        setIysModalMd(null);
                        try {
                            const r = await axiosInstance.get(`/api/CariIysIzinleri/cari/${musteri.cariID}`);
                            setIysIzinlerMd(r.data);
                        } catch {}
                    }}
                />
            )}
        </div>
    );
};

export default MusteriDetay;
